import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store';
import Btn from '../ui/Btn';
import Chip from '../ui/Chip';
import Panel from '../ui/Panel';
import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';
import { Input } from '../ui/Field';
import Logo from '../brand/Logo';
import PremierBadge, { premierTier } from '../cs/PremierBadge';

function statusMeta(s) {
  return s === 'verified' ? { c: 'var(--ok)', l: 'admin.sheet.statusVerified' }
    : s === 'rejected' ? { c: 'var(--danger)', l: 'admin.sheet.statusRejected' }
    : { c: 'var(--warn)', l: 'admin.sheet.statusPending' };
}
function aInitials(n) { return (n || 'PL').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase(); }

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
};
const ROLE_LABEL = { igl: 'IGL', awp: 'AWPer', entry: 'Entry Fragger', support: 'Support', lurk: 'Lurk' };

// backend application → форма для таблицы/модалки
function mapApplication(app) {
  return {
    id: app.id,
    name: app.user?.full_name || app.fio || app.user?.nickname || '—',
    nick: app.user?.nickname || app.game_nick || '—',
    email: app.user?.email || '—',
    steam: app.steam_profile || '—',
    rating: app.premier_rating || 0,
    status: app.status,
    team: app.team ? (app.team_tag ? `${app.team} [${app.team_tag}]` : app.team) : null,
    role: ROLE_LABEL[app.role] || app.role || '—',
    edu: app.education_name ? `${app.education_name}${app.grade ? ' · ' + app.grade : ''}` : '—',
    doc: !!app.document_file,
    docFile: app.document_file || null,
    rejectionReason: app.rejection_reason || null,
    joined: fmtDate(app.created_at),
    userId: app.user_id,
  };
}

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5252/api').replace(/\/api$/, '');
const docUrl = (path) => path ? `${API_ORIGIN}/${String(path).replace(/^\/+/, '')}` : null;

// Документы заявок отдаются бэкендом только владельцу/админу — обычная
// <a href> не пронесёт токен, поэтому качаем с Authorization и открываем blob.
const openAuthedDoc = async (path, { download } = {}) => {
  const url = docUrl(path);
  if (!url) return;
  try {
    const token = localStorage.getItem('access_token');
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    if (download) {
      const a = document.createElement('a');
      a.href = blobUrl; a.download = String(path).split(/[\\/]/).pop() || 'document';
      document.body.appendChild(a); a.click(); a.remove();
    } else {
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

// backend team → форма для карточек/модалки (обогащаем данными заявок по user_id)
const TEAM_STATUS = { recruiting: 'Набор', moderation: 'На модерации', full: 'Подтверждена', finished: 'Завершена' };
function mapTeam(team, appsByUser) {
  const roster = (team.members || []).map(m => {
    const u = m.user || {};
    const a = appsByUser[m.user_id]; // фолбэк для старых данных без обогащения с бэка
    const isCap = m.user_id === team.captain_id;
    const roleBase = ROLE_LABEL[m.role] || m.role || 'Игрок';
    const eduName = u.education_name ?? a?.education_name;
    const grade = u.grade ?? a?.grade;
    return {
      name: m.user?.full_name || m.user?.nickname || '—',
      nick: m.user?.nickname || '—',
      role: isCap ? `Капитан / ${roleBase}` : roleBase,
      rating: u.premier_rating ?? a?.premier_rating ?? 0,
      edu: eduName ? `${eduName}${grade ? ' · ' + grade : ''}` : '—',
      doc: u.has_document ?? !!a?.document_file,
      status: u.application_status || a?.status || 'submitted',
      userId: m.user_id,
      isSub: false,
    };
  });
  const ratings = roster.map(p => p.rating).filter(Boolean);
  const avg = ratings.length ? Math.round(ratings.reduce((s, x) => s + x, 0) / ratings.length) : (team.avg_rating || 0);
  return {
    id: team.id,
    tournament_id: team.tournament_id,
    name: team.name,
    tag: team.tag,
    captain: team.captain?.full_name || team.captain?.nickname || '—',
    players: team.players_count ?? roster.length,
    sub: 0,
    avg,
    status: TEAM_STATUS[team.status] || team.status,
    rawStatus: team.status,
    map: '—',
    contact: team.join_code ? `код: ${team.join_code}` : '—',
    created: fmtDate(team.created_at),
    roster,
  };
}

const T_STATUS = { registration: 'Регистрация', ongoing: 'Идёт', finished: 'Завершён', draft: 'Черновик' };
function mapTournament(t) {
  return { ...t, statusLabel: T_STATUS[t.status] || t.status, teams: t.teams_count ?? t.teams, players: t.players_count ?? t.players };
}

function AdminStat({ label, value, sub, accent }) {
  return (
    <div className="clip-tr" style={{ position: 'relative', overflow: 'hidden', padding: 20, background: 'linear-gradient(160deg, var(--surface-2), var(--surface))', border: '1px solid var(--line)' }}>
      <div style={{ position: 'absolute', top: -20, right: -10, width: 70, height: 70, background: accent ? 'radial-gradient(circle, var(--accent-glow), transparent 70%)' : 'none', filter: 'blur(8px)' }} />
      <div className="mono" style={{ fontSize: 10.5, letterSpacing: '0.16em', color: 'var(--fg-muted)', textTransform: 'uppercase' }}>{label}</div>
      <div className="display" style={{ fontSize: 34, marginTop: 8, color: accent ? 'var(--accent-bright)' : 'var(--fg)' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--ok)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function RegDetailModal({ acc, open, onClose, onAction }) {
  const { t } = useTranslation();
  if (!acc) return null;
  const m = statusMeta(acc.status);
  const tier = premierTier(acc.rating);
  return (
    <Modal open={open} onClose={onClose} width={600}>
      <div style={{ padding: 0 }}>
        <div className="modal-pad" style={{ padding: 'clamp(16px,3vw,24px)', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div className="row gap-3" style={{ alignItems: 'center', minWidth: 0 }}>
            <div className="center display" style={{ width: 44, height: 44, borderRadius: 'var(--r-md)', background: `linear-gradient(135deg, ${tier.c}33, var(--surface-3))`, border: `1px solid ${tier.c}`, fontSize: 15, flexShrink: 0 }}>{aInitials(acc.name)}</div>
            <div className="col" style={{ minWidth: 0 }}>
              <span className="display" style={{ fontSize: 18, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.nick}</span>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.name}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'var(--surface-3)', border: '1px solid var(--line)', color: 'var(--fg)', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </div>
        <div className="modal-2col modal-pad" style={{ padding: 'clamp(14px,3vw,24px)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', color: 'var(--fg-muted)', textTransform: 'uppercase', marginBottom: 10 }}>{t('admin.sheet.document')}</div>
            <div style={{ position: 'relative', height: 180, borderRadius: 'var(--r-md)', overflow: 'hidden', border: `1px solid ${acc.doc ? 'var(--line-strong)' : 'var(--danger)'}`, background: acc.doc ? 'repeating-linear-gradient(135deg, #15151a, #15151a 12px, #18181e 12px, #18181e 24px)' : 'var(--surface)' }}>
              {acc.doc ? (
                <>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10 }}>
                    <div style={{ width: 120, height: 76, borderRadius: 6, background: 'linear-gradient(135deg, var(--surface-3), var(--elevated))', border: '1px solid var(--line-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 8, top: 8, width: 24, height: 24, borderRadius: '50%', background: 'var(--surface-2)', border: '1px solid var(--line)' }} />
                      <span className="mono" style={{ fontSize: 8, color: 'var(--fg-muted)', position: 'absolute', bottom: 8, left: 8 }}>{t('admin.sheet.studentCard')}</span>
                      <Logo size={28} />
                    </div>
                    <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{acc.docFile ? acc.docFile.split(/[\\/]/).pop() : t('admin.sheet.document')}</span>
                  </div>
                  <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6 }}>
                    <button type="button" onClick={() => openAuthedDoc(acc.docFile)} style={{ padding: '5px 10px', background: 'rgba(0,0,0,0.5)', border: '1px solid var(--line)', borderRadius: 6, color: 'var(--fg)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>{t('admin.sheet.open')}</button>
                    <button type="button" onClick={() => openAuthedDoc(acc.docFile, { download: true })} style={{ padding: '5px 10px', background: 'rgba(0,0,0,0.5)', border: '1px solid var(--line)', borderRadius: 6, color: 'var(--fg)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>{t('admin.sheet.download')}</button>
                  </div>
                </>
              ) : (
                <div className="center" style={{ height: '100%', flexDirection: 'column', gap: 8, color: 'var(--danger)' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6m0-6l6 6" /></svg>
                  <span style={{ fontSize: 13 }}>{t('admin.sheet.docMissing')}</span>
                </div>
              )}
            </div>
          </div>
          {[
            [t('admin.modal.fio'), acc.name], [t('admin.modal.nickname'), acc.nick], [t('admin.modal.email'), acc.email], [t('admin.modal.steam'), acc.steam],
            [t('admin.modal.edu'), acc.edu], [t('admin.modal.premier'), acc.rating ? acc.rating.toLocaleString('ru-RU') : '—'],
            [t('admin.modal.team'), acc.team || t('admin.modal.noTeam')], [t('admin.modal.dateApplied'), acc.joined],
          ].map(([l, v], i) => (
            <div key={i} style={{ padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.12em', color: 'var(--fg-muted)', textTransform: 'uppercase' }}>{l}</div>
              <div style={{ fontSize: 13.5, marginTop: 5, wordBreak: 'break-word' }}>{v}</div>
            </div>
          ))}
        </div>
        <div className="row gap-3 modal-pad" style={{ padding: 'clamp(14px,3vw,22px) clamp(14px,3vw,26px)', borderTop: '1px solid var(--line)', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Chip color={m.c} icon={<span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />}>{t(m.l)}</Chip>
          <div className="row gap-3">
            <Btn variant="outline" size="sm" onClick={() => { onAction(acc.id, 'rejected'); onClose(); }}>{t('admin.sheet.reject')}</Btn>
            <Btn variant="gold" size="sm" onClick={() => { onAction(acc.id, 'verified'); onClose(); }} icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a1206" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>}>{t('admin.sheet.approve')}</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const ROLE_COLORS = {
  'Капитан / IGL': '#E6B73C', 'Капитан / AWPer': '#E6B73C', 'Капитан / Rifler': '#E6B73C',
  'AWPer': '#58A6FF', 'IGL': '#A78BFA', 'Rifler': '#4ADE80',
  'Entry Fragger': '#F87171', 'Support': '#94A3B8', 'Запасной': '#5A5A63',
};

function TeamDetailModal({ team, open, onClose, onModerate, onDelete, onPlayerOpen }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  if (!team) return null;
  const onModeration = team.rawStatus === 'moderation';

  const moderate = async (action) => {
    let reason = null;
    if (action === 'reject') {
      reason = window.prompt(t('admin.teamModal.rejectReasonPrompt'));
      if (reason === null) return; // админ отменил действие
    }
    setBusy(true);
    try {
      await onModerate(team.id, action, reason);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t('admin.teamModal.deleteConfirm', { name: team.name }))) return;
    setBusy(true);
    try {
      await onDelete(team.id);
      onClose();
    } finally {
      setBusy(false);
    }
  };
  const confirmed = team.roster.filter(p => p.status === 'verified').length;
  const pending = team.roster.filter(p => p.status === 'pending').length;
  const maxRating = Math.max(...team.roster.map(p => p.rating));
  const allVerified = team.roster.every(p => p.doc);

  return (
    <Modal open={open} onClose={onClose} width={780}>
      {/* Header */}
      <div className="modal-pad" style={{ padding: 'clamp(16px,3vw,24px)', background: 'linear-gradient(135deg, var(--surface-2), var(--surface))', borderBottom: '1px solid var(--line)' }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10 }}>
          <div className="row gap-3" style={{ alignItems: 'center', minWidth: 0, flexWrap: 'wrap' }}>
            <div className="center display" style={{
              width: 50, height: 50, borderRadius: 'var(--r-md)', fontSize: 16, fontWeight: 700,
              background: 'linear-gradient(135deg, var(--accent-deep) 0%, #2a0a0b 100%)',
              border: '1px solid var(--line-accent)', boxShadow: '0 0 20px -6px var(--accent-glow)',
            }}>{team.tag}</div>
            <div className="col">
              <span className="display" style={{ fontSize: 26, lineHeight: 1 }}>{team.name}</span>
              <div className="row gap-2" style={{ marginTop: 6, alignItems: 'center' }}>
                <Chip color={team.status === 'Подтверждена' ? 'var(--ok)' : 'var(--warn)'}>{team.status}</Chip>
                <span className="mono" style={{ fontSize: 10, color: 'var(--fg-muted)', letterSpacing: '0.12em' }}>{t('admin.teams.labelMap')}: {team.map}</span>
                <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', letterSpacing: '0.08em' }}>{team.contact}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'var(--surface-3)', border: '1px solid var(--line)', color: 'var(--fg)', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </div>
        {/* Stats strip */}
        <div className="row gap-3" style={{ flexWrap: 'wrap' }}>
          {[
            { l: t('admin.teamModal.players'), v: `${team.players}/5 +${team.sub}` },
            { l: t('admin.teamModal.verifiedCount'), v: confirmed, c: confirmed === team.roster.length ? 'var(--ok)' : 'var(--warn)' },
            { l: t('admin.teamModal.pendingCount'), v: pending, c: pending ? 'var(--warn)' : 'var(--fg-muted)' },
            { l: t('admin.teamModal.avgRating'), v: team.avg.toLocaleString('ru-RU') },
            { l: t('admin.teamModal.documents'), v: allVerified ? t('admin.teamModal.docsAll') : `${team.roster.filter(p=>p.doc).length}/${team.roster.length}`, c: allVerified ? 'var(--ok)' : 'var(--warn)' },
            { l: t('admin.teamModal.created'), v: team.created },
          ].map(({ l, v, c }) => (
            <div key={l} style={{ padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', minWidth: 100 }}>
              <div className="mono" style={{ fontSize: 9, letterSpacing: '0.16em', color: 'var(--fg-muted)' }}>{l}</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4, color: c || 'var(--fg)', fontFamily: 'var(--font-display)', letterSpacing: '0.02em' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Roster */}
      <div className="modal-pad" style={{ padding: 'clamp(14px,3vw,24px)' }}>
        <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--fg-muted)', marginBottom: 14 }}>{t('admin.teamModal.rosterTitle')} · {t('admin.teamModal.personCount', { n: team.roster.length })}</div>
        <div className="col gap-3">
          {team.roster.map((p, i) => {
            const tier = premierTier(p.rating);
            const roleColor = ROLE_COLORS[p.role] || 'var(--fg-muted)';
            const sm = statusMeta(p.status);
            const isTop = p.rating === maxRating;
            const clickable = !!onPlayerOpen && !!p.userId;
            const baseBorder = p.isSub ? 'var(--line)' : 'var(--line-strong)';
            return (
              <div key={i}
                onClick={clickable ? () => onPlayerOpen(p) : undefined}
                title={clickable ? t('admin.teamModal.openProfile') : undefined}
                onMouseEnter={clickable ? (e) => { e.currentTarget.style.borderColor = 'var(--accent-bright)'; } : undefined}
                onMouseLeave={clickable ? (e) => { e.currentTarget.style.borderColor = baseBorder; } : undefined}
                style={{
                display: 'grid', gridTemplateColumns: '48px 1fr auto', gap: 14, alignItems: 'center',
                padding: '14px 16px', borderRadius: 'var(--r-md)',
                background: p.isSub ? 'var(--surface)' : 'linear-gradient(135deg, var(--surface-2) 0%, var(--surface) 100%)',
                border: `1px solid ${baseBorder}`,
                opacity: p.isSub ? 0.8 : 1,
                cursor: clickable ? 'pointer' : 'default',
                transition: 'border-color .15s ease',
                position: 'relative', overflow: 'hidden',
              }}>
                {isTop && !p.isSub && (
                  <div style={{ position: 'absolute', top: 0, right: 0, background: 'var(--gold)', color: '#1a1206', fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700, padding: '3px 8px', letterSpacing: '0.1em' }}>{t('admin.modal.topRating')}</div>
                )}
                {/* Avatar */}
                <div className="center display" style={{
                  width: 48, height: 48, borderRadius: 'var(--r-md)', fontSize: 15, flexShrink: 0,
                  background: `linear-gradient(135deg, ${tier.c}33, var(--surface-3))`,
                  border: `1px solid ${tier.c}55`,
                }}>{aInitials(p.name)}</div>
                {/* Info */}
                <div>
                  <div className="row gap-2" style={{ alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                    <span className="display" style={{ fontSize: 16 }}>{p.nick}</span>
                    <span style={{ fontSize: 11, color: roleColor, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{p.role}</span>
                    {p.isSub && <span className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', letterSpacing: '0.12em' }}>{t('admin.modal.substitute')}</span>}
                  </div>
                  <div className="row gap-3" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{p.name}</span>
                    <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>·</span>
                    <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{p.edu}</span>
                    <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>·</span>
                    <div className="row gap-1" style={{ alignItems: 'center' }}>
                      {p.doc
                        ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ok)" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                        : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--warn)" strokeWidth="2.5"><path d="M12 9v4m0 4h.01M10.3 4.3L3.7 16A2 2 0 005.4 19h13.2a2 2 0 001.7-3L13.7 4.3a2 2 0 00-3.4 0z"/></svg>
                      }
                      <span className="mono" style={{ fontSize: 10, color: p.doc ? 'var(--ok)' : 'var(--warn)' }}>{p.doc ? t('admin.modal.docPresent') : t('admin.modal.noDoc')}</span>
                    </div>
                  </div>
                </div>
                {/* Right: rating + status (+ переход в профиль) */}
                <div className="row gap-2" style={{ alignItems: 'center' }}>
                <div className="col" style={{ alignItems: 'flex-end', gap: 8 }}>
                  <PremierBadge rating={p.rating} size="sm" />
                  <Chip color={sm.c} style={{ fontSize: 9.5 }}>{t(sm.l)}</Chip>
                </div>
                {clickable && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--fg-faint)" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M9 18l6-6-6-6" /></svg>
                )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="row gap-3" style={{ marginTop: 20, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <Btn variant="ghost" size="sm" disabled={busy} onClick={remove} style={{ color: 'var(--danger)' }} icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2.2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/></svg>}>{t('admin.teamModal.deleteBtn')}</Btn>
          <div className="row gap-3">
            <Btn variant="outline" size="sm" onClick={onClose}>{t('admin.modal.closeBtn')}</Btn>
            {onModeration && (
              <>
                <Btn variant="outline" size="sm" disabled={busy} onClick={() => moderate('reject')} style={{ color: 'var(--danger)', boxShadow: 'inset 0 0 0 1.5px var(--danger)' }}>{t('admin.modal.rejectTeam')}</Btn>
                <Btn variant="gold" size="sm" disabled={busy} onClick={() => moderate('approve')} icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1a1206" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>}>{t('admin.modal.approveTeam')}</Btn>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

const ALL_MAPS = ['Mirage', 'Dust2', 'Inferno', 'Ancient', 'Nuke', 'Anubis', 'Vertigo'];

function ModalTab({ tabs, active, onChange }) {
  return (
    <div className="row" style={{ borderBottom: '1px solid var(--line)', padding: '0 clamp(12px,3vw,28px)', gap: 0, overflowX: 'auto' }}>
      {tabs.map(t => (
        <button key={t.k} onClick={() => onChange(t.k)} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '14px 18px', position: 'relative',
          fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em',
          color: active === t.k ? 'var(--fg)' : 'var(--fg-muted)', transition: 'color .2s',
        }}>
          {t.l}
          {active === t.k && <span style={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, background: 'var(--accent)' }} />}
        </button>
      ))}
    </div>
  );
}

function TournamentManageModal({ t: tournament, teams = [], open, onClose, onUpdate }) {
  const { t } = useTranslation();
  const [inner, setInner] = useState('overview');
  const [regOpen, setRegOpen] = useState(!!tournament?.reg_open);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tournament) {
      setRegOpen(!!tournament.reg_open);
      setForm({ title: tournament.title || '', date: tournament.date || '', prize: tournament.prize || '', fee: tournament.fee || '', format: tournament.format || '', telegram: tournament.telegram || '' });
    }
  }, [tournament]);

  if (!tournament) return null;

  const teamsInTournament = teams;

  const saveSettings = async () => {
    setSaving(true);
    try { await onUpdate(tournament.id, { ...form, reg_open: regOpen }); onClose(); }
    finally { setSaving(false); }
  };
  const toggleReg = async () => {
    const v = !regOpen; setRegOpen(v);
    await onUpdate(tournament.id, { reg_open: v });
  };

  return (
    <Modal open={open} onClose={onClose} width={860}>
      {/* Header */}
      <div className="modal-pad" style={{ padding: 'clamp(14px,3vw,22px) clamp(14px,3vw,28px)', borderBottom: '1px solid var(--line)', background: 'linear-gradient(135deg, var(--surface-2), var(--surface))' }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div className="row gap-3" style={{ alignItems: 'center', minWidth: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: 'var(--r-md)', overflow: 'hidden', border: '1px solid var(--line)', flexShrink: 0 }}>
              <img src="/assets/logo-federation.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div className="col">
              <span className="display" style={{ fontSize: 22 }}>{tournament.title}</span>
              <div className="row gap-2" style={{ marginTop: 5, alignItems: 'center' }}>
                <Chip color={tournament.status === 'registration' ? 'var(--accent)' : tournament.status === 'finished' ? 'var(--fg-muted)' : 'var(--ok)'}>{tournament.statusLabel}</Chip>
                <span className="mono" style={{ fontSize: 10, color: 'var(--fg-muted)', letterSpacing: '0.12em' }}>{tournament.date} · {tournament.prize}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'var(--surface-3)', border: '1px solid var(--line)', color: 'var(--fg)', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </div>
      </div>

      <ModalTab tabs={[
        {k:'overview',l:t('admin.tournamentModal.innerOverview')},
        {k:'teams',l:t('admin.tournamentModal.innerTeams')},
        {k:'bracket',l:t('admin.tournamentModal.innerBracket')},
        {k:'settings',l:t('admin.tournamentModal.innerSettings')},
      ]} active={inner} onChange={setInner} />

      <div className="modal-pad" style={{ padding: 'clamp(14px,3vw,24px)' }}>

        {/* ── ОБЗОР ── */}
        {inner === 'overview' && (
          <div className="col gap-5">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 12 }}>
              {[
                { l: t('admin.tournamentModal.statTeams'), v: tournament.teams, c: 'var(--accent)' },
                { l: t('admin.tournamentModal.statPlayers'), v: tournament.players },
                { l: t('admin.tournamentModal.statLimit'), v: tournament.teams_cap },
                { l: t('admin.tournamentModal.statReg'), v: regOpen ? t('admin.tournamentModal.regOpen') : t('admin.tournamentModal.regClosed'), c: regOpen ? 'var(--ok)' : 'var(--warn)' },
              ].map(({ l, v, c }) => (
                <div key={l} style={{ padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
                  <div className="mono" style={{ fontSize: 9, letterSpacing: '0.16em', color: 'var(--fg-muted)' }}>{l}</div>
                  <div className="display" style={{ fontSize: 30, marginTop: 6, color: c || 'var(--fg)' }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div style={{ padding: '18px 20px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>{t('admin.tournamentModal.fillLabel')}</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--accent)' }}>{tournament.teams} / {tournament.teams_cap}</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: 'var(--surface-3)' }}>
                <div style={{ height: '100%', borderRadius: 4, width: `${Math.min(100, tournament.teams / (tournament.teams_cap || 64) * 100)}%`, background: 'linear-gradient(90deg, var(--accent-deep), var(--accent-bright))', transition: 'width .6s' }} />
              </div>
            </div>

            {/* Timeline */}
            <div style={{ padding: '18px 20px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
              <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)', marginBottom: 14 }}>{t('admin.tournamentModal.timeline')}</div>
              <div className="col gap-3">
                {[
                  { d: fmtDate(tournament.created_at), l: t('admin.tournamentModal.tlOpen'), done: true },
                  { d: '—', l: t('admin.tournamentModal.tlClose'), done: !tournament.reg_open },
                  { d: '—', l: t('admin.tournamentModal.tlDraw'), done: tournament.status === 'finished' },
                  { d: tournament.date, l: t('admin.tournamentModal.tlTournament'), done: tournament.status === 'finished' },
                ].map(({ d, l, done }, i) => (
                  <div key={i} className="row gap-3" style={{ alignItems: 'center' }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: done ? 'var(--ok)' : 'var(--surface-3)', border: `2px solid ${done ? 'var(--ok)' : 'var(--line-strong)'}`, flexShrink: 0 }} />
                    <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', minWidth: 70 }}>{d}</span>
                    <span style={{ fontSize: 13, color: done ? 'var(--fg)' : 'var(--fg-muted)' }}>{l}</span>
                    {done && <span style={{ fontSize: 10, color: 'var(--ok)', marginLeft: 'auto', fontFamily: 'var(--font-mono)' }}>✓</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="row gap-3" style={{ flexWrap: 'wrap' }}>
              <div className="row gap-2" style={{ alignItems: 'center', padding: '10px 16px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
                <span style={{ fontSize: 13 }}>{t('admin.tournamentModal.regToggle')}</span>
                <div onClick={toggleReg} style={{ width: 46, height: 26, borderRadius: 20, background: regOpen ? 'var(--accent)' : 'var(--surface-3)', position: 'relative', cursor: 'pointer', transition: 'background .2s', border: '1px solid var(--line)' }}>
                  <span style={{ position: 'absolute', top: 2, left: regOpen ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                </div>
              </div>
              <Btn variant="outline" size="sm" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>}>{t('admin.tournamentModal.editBtn')}</Btn>
              <Btn variant="dark" size="sm" icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"/></svg>}>{t('admin.tournamentModal.exportBtn')}</Btn>
            </div>
          </div>
        )}

        {/* ── КОМАНДЫ ── */}
        {inner === 'teams' && (
          <div className="col gap-3">
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', color: 'var(--fg-muted)' }}>{t('admin.tournaments.colTeams')} · {tournament.teams} ({teamsInTournament.length})</div>
            {teamsInTournament.map((tm, i) => (
              <div key={tm.id} className="row" style={{ alignItems: 'center', gap: 14, padding: '12px 16px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', flexWrap: 'wrap' }}>
                <span className="mono" style={{ fontSize: 12, color: 'var(--fg-faint)', minWidth: 22 }}>#{i + 1}</span>
                <div className="center display" style={{ width: 38, height: 38, borderRadius: 8, background: 'linear-gradient(135deg, var(--accent-deep), var(--surface-3))', fontSize: 12, border: '1px solid var(--line)' }}>{tm.tag}</div>
                <div className="col grow">
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{tm.name}</span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--fg-muted)', marginTop: 1 }}>{tm.captain} · {tm.players}/5 +{tm.sub}</span>
                </div>
                <div className="row gap-3" style={{ alignItems: 'center' }}>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{tm.map}</span>
                  <PremierBadge rating={tm.avg} size="sm" />
                  <Chip color={tm.status === 'Подтверждена' ? 'var(--ok)' : 'var(--warn)'} style={{ fontSize: 10 }}>{tm.status}</Chip>
                </div>
              </div>
            ))}
            {tournament.teams > teamsInTournament.length && (
              <div className="mono" style={{ textAlign: 'center', fontSize: 11, color: 'var(--fg-faint)', padding: 12 }}>{t('admin.tournamentModal.moreTeams', { n: tournament.teams - teamsInTournament.length })}</div>
            )}
          </div>
        )}

        {/* ── СЕТКА ── */}
        {inner === 'bracket' && (
          <div className="col gap-4">
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', color: 'var(--fg-muted)' }}>{t('admin.tournamentModal.bracketType')}</div>
            <div className="center" style={{ height: 180, flexDirection: 'column', gap: 10, color: 'var(--fg-muted)', border: '1px dashed var(--line)', borderRadius: 'var(--r-md)' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 9H4a2 2 0 0 1-2-2V5h4M18 9h2a2 2 0 0 0 2-2V5h-4M6 5h12v6a6 6 0 0 1-12 0zM9 19h6M10 22h4M12 17v2"/></svg>
              <span style={{ fontSize: 14 }}>{t('admin.tournamentModal.bracketEmpty')} · {tournament.teams}</span>
            </div>
          </div>
        )}

        {/* ── НАСТРОЙКИ ── */}
        {inner === 'settings' && (
          <div className="col gap-4">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
              {[
                { l: t('admin.tournamentModal.fieldName'), k: 'title' },
                { l: t('admin.tournamentModal.fieldDate'), k: 'date' },
                { l: t('admin.tournamentModal.fieldPrize'), k: 'prize' },
                { l: t('admin.tournamentModal.fieldFee'), k: 'fee' },
                { l: t('admin.tournamentModal.fieldFormat'), k: 'format' },
                { l: 'Telegram', k: 'telegram' },
              ].map(({ l, k }) => (
                <div key={k} className="col gap-2">
                  <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>{l.toUpperCase()}</span>
                  <input value={form[k] || ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={{ height: 42, padding: '0 14px', background: 'var(--surface)', color: 'var(--fg)', fontFamily: 'var(--font-body)', fontSize: 14, border: '1px solid var(--line-strong)', borderRadius: 'var(--r-md)', outline: 'none' }} />
                </div>
              ))}
            </div>
            <div className="col gap-2">
              <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>{t('admin.tournamentModal.fieldMaps')}</span>
              <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                {ALL_MAPS.map(m => {
                  const active = ['Mirage','Dust2','Inferno','Ancient','Nuke'].includes(m);
                  return (
                    <button key={m} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${active ? 'var(--accent)' : 'var(--line)'}`, background: active ? 'rgba(225,18,23,0.12)' : 'var(--surface)', color: active ? 'var(--fg)' : 'var(--fg-muted)', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', transition: 'all .15s' }}>{m}</button>
                  );
                })}
              </div>
            </div>
            <div className="row gap-3" style={{ justifyContent: 'flex-end', marginTop: 4 }}>
              <Btn variant="outline" size="sm" onClick={onClose}>{t('admin.tournamentModal.cancelBtn')}</Btn>
              <Btn size="sm" disabled={saving} onClick={saveSettings} icon={saving ? <Spinner size={14} /> : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2.5"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>}>{saving ? t('admin.tournamentModal.saving') : t('admin.tournamentModal.saveBtn')}</Btn>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function TournamentCreateModal({ open, onClose, onCreate }) {
  const { t } = useTranslation();
  const [maps, setMaps] = useState(['Mirage','Dust2','Inferno','Ancient','Nuke']);
  const [form, setForm] = useState({ title: '', date: '', location: '', format: 'single', fee: '10 000 ₸', prize: '', telegram: '', eligibility: '', roster: '5+1' });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleMap = m => setMaps(ms => ms.includes(m) ? ms.filter(x => x !== m) : [...ms, m]);

  const FORMAT_LABEL = { single: 'Single Elimination', double: 'Double Elimination', groups: 'Группы + Плей-офф' };
  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await onCreate({
        title: form.title.trim(),
        date: form.date,
        place: form.location,
        format: FORMAT_LABEL[form.format] || form.format,
        fee: form.fee,
        prize: form.prize,
        telegram: form.telegram,
        eligibility: form.eligibility,
        roster: form.roster,
        maps,
        status: 'registration',
        reg_open: true,
      });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} width={720}>
      {/* Header */}
      <div className="modal-pad" style={{ padding: 'clamp(14px,3vw,22px) clamp(14px,3vw,28px)', borderBottom: '1px solid var(--line)', background: 'linear-gradient(135deg, var(--surface-2), var(--surface))' }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div className="col">
            <span className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--accent)' }}>{t('admin.createModal.badge')}</span>
            <span className="display" style={{ fontSize: 24, marginTop: 4 }}>{t('admin.createModal.title')}</span>
          </div>
          <button onClick={onClose} style={{ background: 'var(--surface-3)', border: '1px solid var(--line)', color: 'var(--fg)', width: 34, height: 34, borderRadius: 8, cursor: 'pointer' }}>✕</button>
        </div>
      </div>

      <div className="modal-pad" style={{ padding: 'clamp(14px,3vw,24px)' }}>
        <div className="col gap-5">

          {/* Основное */}
          <div className="col gap-3">
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--fg-muted)', paddingBottom: 8, borderBottom: '1px solid var(--line)' }}>{t('admin.createModal.sectionMain')}</div>
            <div className="modal-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="col gap-2" style={{ gridColumn: '1 / -1' }}>
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>{t('admin.createModal.fieldName')}</span>
                <input value={form.title} onChange={set('title')} placeholder={t('admin.createModal.namePlaceholder')} style={{ height: 44, padding: '0 14px', background: 'var(--surface)', color: 'var(--fg)', fontFamily: 'var(--font-body)', fontSize: 14, border: '1px solid var(--line-strong)', borderRadius: 'var(--r-md)', outline: 'none' }} />
              </div>
              <div className="col gap-2">
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>{t('admin.createModal.fieldDate')}</span>
                <input type="date" value={form.date} onChange={set('date')} style={{ height: 44, padding: '0 14px', background: 'var(--surface)', color: 'var(--fg)', fontFamily: 'var(--font-body)', fontSize: 14, border: '1px solid var(--line-strong)', borderRadius: 'var(--r-md)', outline: 'none', colorScheme: 'dark' }} />
              </div>
              <div className="col gap-2">
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>{t('admin.createModal.fieldLocation')}</span>
                <input value={form.location} onChange={set('location')} placeholder={t('admin.createModal.locationPlaceholder')} style={{ height: 44, padding: '0 14px', background: 'var(--surface)', color: 'var(--fg)', fontFamily: 'var(--font-body)', fontSize: 14, border: '1px solid var(--line-strong)', borderRadius: 'var(--r-md)', outline: 'none' }} />
              </div>
            </div>
          </div>

          {/* Формат */}
          <div className="col gap-3">
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--fg-muted)', paddingBottom: 8, borderBottom: '1px solid var(--line)' }}>{t('admin.createModal.sectionFormat')}</div>
            <div className="row gap-3" style={{ flexWrap: 'wrap' }}>
              {[['single','Single Elimination'],['double','Double Elimination'],['groups', t('admin.createModal.formatGroups')]].map(([v,l]) => (
                <button key={v} onClick={() => setForm(f=>({...f,format:v}))} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${form.format===v?'var(--accent)':'var(--line)'}`, background: form.format===v?'rgba(225,18,23,0.12)':'var(--surface)', color: form.format===v?'var(--fg)':'var(--fg-muted)', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', transition: 'all .15s' }}>{l}</button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 14 }}>
              <div className="col gap-2">
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>{t('admin.createModal.fieldRoster')}</span>
                <select value={form.roster} onChange={set('roster')} style={{ height: 44, padding: '0 14px', background: 'var(--surface)', color: 'var(--fg)', fontFamily: 'var(--font-body)', fontSize: 14, border: '1px solid var(--line-strong)', borderRadius: 'var(--r-md)', outline: 'none', cursor: 'pointer' }}>
                  {['5+0','5+1','5+2','6+0'].map(o=><option key={o} value={o} style={{background:'var(--surface)'}}>{o}</option>)}
                </select>
              </div>
              <div className="col gap-2">
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>{t('admin.createModal.fieldFee')}</span>
                <input value={form.fee} onChange={set('fee')} placeholder={t('admin.createModal.feePlaceholder')} style={{ height: 44, padding: '0 14px', background: 'var(--surface)', color: 'var(--fg)', fontFamily: 'var(--font-body)', fontSize: 14, border: '1px solid var(--line-strong)', borderRadius: 'var(--r-md)', outline: 'none' }} />
              </div>
              <div className="col gap-2">
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>{t('admin.createModal.fieldPrize')}</span>
                <input value={form.prize} onChange={set('prize')} placeholder={t('admin.createModal.prizePlaceholder')} style={{ height: 44, padding: '0 14px', background: 'var(--surface)', color: 'var(--fg)', fontFamily: 'var(--font-body)', fontSize: 14, border: '1px solid var(--line-strong)', borderRadius: 'var(--r-md)', outline: 'none' }} />
              </div>
            </div>
          </div>

          {/* Пулл карт */}
          <div className="col gap-3">
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--fg-muted)', paddingBottom: 8, borderBottom: '1px solid var(--line)' }}>{t('admin.createModal.fieldMaps')} ({t('admin.createModal.mapsSelected', { n: maps.length })})</div>
            <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
              {ALL_MAPS.map(m => {
                const on = maps.includes(m);
                return (
                  <button key={m} onClick={() => toggleMap(m)} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${on?'var(--accent)':'var(--line)'}`, background: on?'rgba(225,18,23,0.12)':'var(--surface)', color: on?'var(--fg)':'var(--fg-muted)', fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer', transition: 'all .15s', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {on && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                    {m}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Доп. настройки */}
          <div className="col gap-3">
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--fg-muted)', paddingBottom: 8, borderBottom: '1px solid var(--line)' }}>{t('admin.createModal.sectionExtra')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="col gap-2">
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>TELEGRAM</span>
                <input value={form.telegram} onChange={set('telegram')} placeholder="@cs2_sko" style={{ height: 44, padding: '0 14px', background: 'var(--surface)', color: 'var(--fg)', fontFamily: 'var(--font-body)', fontSize: 14, border: '1px solid var(--line-strong)', borderRadius: 'var(--r-md)', outline: 'none' }} />
              </div>
              <div className="col gap-2">
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)' }}>{t('admin.createModal.fieldEligibility')}</span>
                <input value={form.eligibility} onChange={set('eligibility')} placeholder={t('admin.createModal.eligibilityPlaceholder')} style={{ height: 44, padding: '0 14px', background: 'var(--surface)', color: 'var(--fg)', fontFamily: 'var(--font-body)', fontSize: 14, border: '1px solid var(--line-strong)', borderRadius: 'var(--r-md)', outline: 'none' }} />
              </div>
            </div>
            <div className="col gap-3">
              {[
                [t('admin.createModal.optRegOpen'), true],
                [t('admin.createModal.optDoc'), true],
                [t('admin.createModal.optSteam'), true],
                [t('admin.createModal.optTelegram'), false],
              ].map(([l, def], i) => (
                <div key={i} className="row" style={{ justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
                  <span style={{ fontSize: 13.5 }}>{l}</span>
                  <div style={{ width: 46, height: 26, borderRadius: 20, background: def?'var(--accent)':'var(--surface-3)', position: 'relative', cursor: 'pointer', border: '1px solid var(--line)', flexShrink: 0 }}>
                    <span style={{ position: 'absolute', top: 2, left: def?22:2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="row gap-3" style={{ justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--line)' }}>
            <Btn variant="outline" size="sm" onClick={onClose}>{t('admin.createModal.cancelBtn')}</Btn>
            <Btn size="sm" disabled={saving || !form.title.trim()} onClick={submit} icon={saving ? <Spinner size={14} /> : <span style={{fontSize:16,lineHeight:1}}>+</span>}>{saving ? t('admin.createModal.creating') : t('admin.createModal.createBtn')}</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ── Notify section ───────────────────────────────── */
const NOTIFY_TYPES = [
  { k: 'info',    c: '#58a6ff' },
  { k: 'success', c: '#56d364' },
  { k: 'warning', c: '#f5b942' },
  { k: 'alert',   c: '#ff3b3b' },
];

const NOTIF_TYPE_META = {
  info:    { c: '#58a6ff' },
  success: { c: '#56d364' },
  warning: { c: '#f5b942' },
  alert:   { c: '#ff3b3b' },
};

function relTimeShort(iso, t) {
  const d = Date.now() - new Date(iso).getTime();
  if (d < 60000) return t('notifications.justNow');
  if (d < 3600000) return t('notifications.minutesAgo', { n: Math.floor(d / 60000) });
  if (d < 86400000) return t('notifications.hoursAgo', { n: Math.floor(d / 3600000) });
  return t('notifications.daysAgo', { n: Math.floor(d / 86400000) });
}

function NotifySection({ users, sendNotification, notifications }) {
  const { t } = useTranslation();
  const [toMode, setToMode] = useState('all');      // 'all' | 'user'
  const [selUserId, setSelUserId] = useState('');
  const [userQ, setUserQ] = useState('');
  const [type, setType] = useState('info');
  const [title, setTitle] = useState('');
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const filteredUsers = userQ.trim()
    ? users.filter(u => (u.nickname || u.email || '').toLowerCase().includes(userQ.toLowerCase()))
    : users.slice(0, 8);

  const canSend = title.trim() && msg.trim() && (toMode === 'all' || selUserId);

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await sendNotification({ to: toMode === 'all' ? 'all' : Number(selUserId), title: title.trim(), message: msg.trim(), type });
      setSent(true);
      setTitle(''); setMsg(''); setSelUserId(''); setUserQ('');
      setTimeout(() => setSent(false), 2500);
    } catch (e) {
      /* ошибка уже показана тостом в store */
    } finally {
      setSending(false);
    }
  };

  const sentList = notifications.slice(0, 30);
  const typeMeta = NOTIF_TYPE_META[type] || NOTIF_TYPE_META.info;

  return (
    <div className="col gap-5">
      <div>
        <h1 className="display" style={{ fontSize: 30, margin: 0 }}>{t('admin.notifs.title')}</h1>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 14 }}>{t('admin.notifs.subtitle')}</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,0.9fr)', gap: 24, alignItems: 'start' }} className="two-col">

        {/* ── Compose form ── */}
        <Panel accent>
          <div className="eyebrow" style={{ marginBottom: 14 }}>{t('admin.notifs.newNotif')}</div>

          {/* Recipient */}
          <div style={{ marginBottom: 20 }}>
            <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--fg-muted)', textTransform: 'uppercase', marginBottom: 10 }}>{t('admin.notifs.recipient')}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[[`all`, t('admin.notifs.allUsers')], [`user`, t('admin.notifs.specificUser')]].map(([k, l]) => (
                <button key={k} onClick={() => setToMode(k)} style={{
                  flex: 1, padding: '9px 12px', borderRadius: 'var(--r-md)', cursor: 'pointer',
                  border: `1px solid ${toMode === k ? 'var(--accent)' : 'var(--line)'}`,
                  background: toMode === k ? 'rgba(225,18,23,0.1)' : 'var(--surface)',
                  color: toMode === k ? 'var(--accent-bright)' : 'var(--fg-muted)',
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, textTransform: 'uppercase',
                  letterSpacing: '0.04em', transition: 'all .18s',
                }}>{l}</button>
              ))}
            </div>
            {toMode === 'user' && (
              <div style={{ position: 'relative' }}>
                <input
                  placeholder={t('admin.notifs.searchUser')}
                  value={userQ}
                  onChange={e => { setUserQ(e.target.value); setSelUserId(''); }}
                  style={{ width: '100%', padding: '10px 14px', background: 'var(--surface)', border: `1px solid ${selUserId ? 'var(--ok)' : 'var(--line)'}`,
                    borderRadius: 'var(--r-md)', color: 'var(--fg)', fontSize: 14, fontFamily: 'var(--font-body)', boxSizing: 'border-box',
                    outline: 'none', transition: 'border-color .18s' }}
                />
                {userQ && filteredUsers.length > 0 && !selUserId && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4,
                    background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
                    overflow: 'hidden', boxShadow: 'var(--shadow-pop)' }}>
                    {filteredUsers.map(u => (
                      <div key={u.id} onClick={() => { setSelUserId(String(u.id)); setUserQ(u.nickname || u.email || `ID ${u.id}`); }}
                        style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', borderBottom: '1px solid var(--line)', transition: 'background .12s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-3)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ fontSize: 14 }}>{u.nickname || u.email}</span>
                        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>ID {u.id}</span>
                      </div>
                    ))}
                  </div>
                )}
                {selUserId && (
                  <div className="mono" style={{ fontSize: 11, color: 'var(--ok)', marginTop: 6, letterSpacing: '0.08em' }}>
                    {t('admin.notifs.selectedUser', { id: selUserId })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Type */}
          <div style={{ marginBottom: 20 }}>
            <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--fg-muted)', textTransform: 'uppercase', marginBottom: 10 }}>{t('admin.notifs.typeLabel')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {NOTIFY_TYPES.map(nt => (
                <button key={nt.k} onClick={() => setType(nt.k)} style={{
                  padding: '7px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                  fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: '0.06em',
                  border: `1px solid ${type === nt.k ? nt.c : 'var(--line)'}`,
                  background: type === nt.k ? `${nt.c}1a` : 'transparent',
                  color: type === nt.k ? nt.c : 'var(--fg-muted)',
                  transition: 'all .18s',
                }}>{t(`admin.notifs.types.${nt.k}`)}</button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div style={{ marginBottom: 16 }}>
            <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--fg-muted)', textTransform: 'uppercase', marginBottom: 8 }}>{t('admin.notifs.titleLabel')}</div>
            <input
              placeholder={t('admin.notifs.titlePlaceholder')}
              value={title}
              maxLength={80}
              onChange={e => setTitle(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)', color: 'var(--fg)', fontSize: 14, fontFamily: 'var(--font-body)',
                boxSizing: 'border-box', outline: 'none', transition: 'border-color .18s' }}
              onFocus={e => e.target.style.borderColor = 'var(--line-accent)'}
              onBlur={e => e.target.style.borderColor = 'var(--line)'}
            />
            <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 4, textAlign: 'right' }}>{title.length}/80</div>
          </div>

          {/* Message */}
          <div style={{ marginBottom: 24 }}>
            <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--fg-muted)', textTransform: 'uppercase', marginBottom: 8 }}>{t('admin.notifs.msgLabel')}</div>
            <textarea
              placeholder={t('admin.notifs.msgPlaceholder')}
              value={msg}
              maxLength={400}
              rows={4}
              onChange={e => setMsg(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)', color: 'var(--fg)', fontSize: 14, fontFamily: 'var(--font-body)',
                resize: 'vertical', boxSizing: 'border-box', outline: 'none', lineHeight: 1.6, transition: 'border-color .18s' }}
              onFocus={e => e.target.style.borderColor = 'var(--line-accent)'}
              onBlur={e => e.target.style.borderColor = 'var(--line)'}
            />
            <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 4, textAlign: 'right' }}>{msg.length}/400</div>
          </div>

          {/* Preview chip */}
          {(title || msg) && (
            <div style={{ padding: '12px 16px', borderRadius: 'var(--r-md)', marginBottom: 20,
              background: `${typeMeta.c}10`, border: `1px solid ${typeMeta.c}33` }}>
              <div className="mono" style={{ fontSize: 10, color: typeMeta.c, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                {t('admin.notifs.previewLabel')} · {t(`admin.notifs.types.${type}`)}
              </div>
              {title && <div className="display" style={{ fontSize: 15, marginBottom: title && msg ? 4 : 0 }}>{title}</div>}
              {msg && <div style={{ fontSize: 13, color: 'var(--fg-soft)', lineHeight: 1.5 }}>{msg}</div>}
            </div>
          )}

          <button
            disabled={!canSend || sending}
            onClick={submit}
            style={{
              width: '100%', padding: '13px', borderRadius: 'var(--r-md)', cursor: canSend ? 'pointer' : 'not-allowed',
              border: 'none', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15,
              textTransform: 'uppercase', letterSpacing: '0.06em',
              background: sent ? 'var(--ok)' : canSend ? 'var(--accent)' : 'var(--surface-3)',
              color: canSend || sent ? '#fff' : 'var(--fg-faint)',
              transition: 'background .25s, color .25s',
              boxShadow: (canSend && !sent) ? '0 0 20px -4px var(--accent-glow)' : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {sent ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                {t('admin.notifs.sentOk')}
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                {toMode === 'all' ? t('admin.notifs.sendToAll') : t('admin.notifs.sendToUser')}
              </>
            )}
          </button>
        </Panel>

        {/* ── History ── */}
        <div className="col gap-3">
          <div className="eyebrow">{t('admin.notifs.historyTitle')}</div>
          {sentList.length === 0 ? (
            <Panel>
              <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--fg-faint)', fontSize: 14 }}>
                {t('admin.notifs.noHistory')}
              </div>
            </Panel>
          ) : (
            sentList.map(n => {
              const meta = NOTIF_TYPE_META[n.type] || NOTIF_TYPE_META.info;
              return (
                <div key={n.id} style={{ padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--line)',
                  borderRadius: 'var(--r-md)', borderLeft: `3px solid ${meta.c}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                    <div className="display" style={{ fontSize: 14, fontWeight: 700 }}>{n.title}</div>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', flexShrink: 0, letterSpacing: '0.06em' }}>
                      {relTimeShort(n.created_at, t)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.5, marginBottom: 8 }}>{n.message}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="mono" style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10,
                      background: `${meta.c}18`, color: meta.c, letterSpacing: '0.08em' }}>
                      {t(`admin.notifs.types.${n.type || 'info'}`)}
                    </span>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)', letterSpacing: '0.08em' }}>
                      → {n.to === 'all' ? t('admin.notifs.allUsers') : `ID ${n.to}`}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ADMIN MOBILE  — полноэкранный интерфейс для смартфонов
   ═══════════════════════════════════════════════════════════ */

/* ---- SVG icon helper ---- */
function AmIcon({ name, size = 20, stroke = 1.9 }) {
  const s = { strokeLinecap: 'round', strokeLinejoin: 'round' };
  const paths = {
    chevron: <path d="M9 18l6-6-6-6" />,
    back: <path d="M15 18l-6-6 6-6" />,
    check: <path d="M20 6L9 17l-5-5" />,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
    bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>,
    grid: <><rect x="3" y="3" width="7.5" height="7.5" rx="1.6" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6" /><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6" /></>,
    clipboard: <><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /></>,
    users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    trophy: <path d="M6 9H4a2 2 0 0 1-2-2V5h4M18 9h2a2 2 0 0 0 2-2V5h-4M6 5h12v6a6 6 0 0 1-12 0zM9 19h6M10 22h4M12 17v2" />,
    gear: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H1a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 7" /></>,
    xcircle: <><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    send: <><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></>,
    person: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
    chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
    more: <><circle cx="5" cy="12" r="1.7" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.7" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1.7" fill="currentColor" stroke="none" /></>,
    edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4z" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></>,
    bracket: <><path d="M6 4v5a3 3 0 0 0 3 3h6" /><path d="M6 20v-5a3 3 0 0 1 3-3" /><path d="m15 8 4 4-4 4" /></>,
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 10v10h14V10M9 20v-6h6v6" /></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} {...s}>
      {paths[name]}
    </svg>
  );
}

function AmChev() { return <span className="am-chev"><AmIcon name="chevron" size={18} stroke={2.2} /></span>; }

function AmSeg({ value, options, onChange }) {
  const idx = Math.max(0, options.findIndex(o => o.k === value));
  const w = `calc((100% - 6px) / ${options.length})`;
  return (
    <div className="am-seg">
      <div className="am-seg-thumb" style={{ width: w, transform: `translateX(calc(${idx} * 100%))` }} />
      {options.map(o => (
        <button key={o.k} className={'am-seg-btn' + (o.k === value ? ' on' : '')} onClick={() => onChange(o.k)}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function AmToggle({ on, onClick }) {
  return (
    <div className={'am-tg' + (on ? ' on' : '')} onClick={onClick} role="switch" aria-checked={on}>
      <span className="am-tg-k" />
    </div>
  );
}

function AmAvatar({ name, rating }) {
  const tier = premierTier(rating || 0);
  const initials = (name || 'PL').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return (
    <div className="am-av" style={{ background: `linear-gradient(140deg, ${tier.c}2e, var(--surface-3))`, border: `1px solid ${tier.c}55` }}>
      {initials}
    </div>
  );
}

/* ---- Tab definitions ---- */
const AM_TAB_KEYS = [
  { k: 'overview', ico: 'grid' },
  { k: 'regs', ico: 'clipboard' },
  { k: 'teams', ico: 'users' },
  { k: 'tournaments', ico: 'trophy' },
  { k: 'more', ico: 'more' },
];

/* ---- Screens ---- */
function MOverviewScreen({ accounts, counts, teams, onOpen, onTab, onSub }) {
  const { t } = useTranslation();
  const bars = [40, 62, 48, 80, 55, 95, 70];
  const days = t('admin.mobile.daysShort', { returnObjects: true });
  return (
    <div className="am-stagger">
      <div className="am-stats" style={{ '--i': 0 }}>
        <div className="am-stat accent">
          <div className="am-stat-l">{t('admin.overview.statApps')}</div>
          <div className="am-stat-v accent">{counts.all}</div>
          <div className="am-stat-s">{t('admin.mobile.statActive', { n: counts.pending })}</div>
        </div>
        <div className="am-stat">
          <div className="am-stat-l">{t('admin.overview.statPending')}</div>
          <div className="am-stat-v">{counts.pending}</div>
          <div className="am-stat-s" style={{ color: 'var(--warn)' }}>{t('admin.mobile.statAwait')}</div>
        </div>
        <div className="am-stat">
          <div className="am-stat-l">{t('admin.overview.statTeams')}</div>
          <div className="am-stat-v">{teams.length}</div>
          <div className="am-stat-s">{teams.filter(tm => tm.status === 'Набор').length} {t('admin.mobile.statRecruiting')}</div>
        </div>
        <div className="am-stat">
          <div className="am-stat-l">{t('admin.overview.statVerified')}</div>
          <div className="am-stat-v">{counts.verified}</div>
          <div className="am-stat-s" style={{ color: 'var(--ok)' }}>{t('admin.mobile.statVerified')}</div>
        </div>
      </div>

      <div className="am-group" style={{ '--i': 1, marginTop: 26 }}>
        <div className="am-ghead">{t('admin.overview.chartTitle')}</div>
        <div className="am-card">
          <div className="am-bars">
            {bars.map((h, i) => (
              <div key={i} className="am-bar-col">
                <div className={'am-bar' + (i === 5 ? ' peak' : '')} style={{ height: `${h}%`, '--bi': i }} />
                <span className="am-bar-x">{Array.isArray(days) ? days[i] : ''}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="am-group" style={{ '--i': 2 }}>
        <div className="am-ghead">
          {t('admin.overview.recentApps')}
          <button className="am-ghead-act" onClick={() => onTab('regs')}>{t('admin.overview.allBtn')}</button>
        </div>
        <div className="am-inset">
          {accounts.slice(0, 5).map((a, i) => {
            const m = amMobileStatus(a.status);
            return (
              <div key={a.id} className="am-row tap" style={{ '--i': i }} onClick={() => onOpen(a)}>
                <AmAvatar name={a.name} rating={a.rating} />
                <div className="am-row-main">
                  <div className="am-row-t">{a.nick}</div>
                  <div className="am-row-s">{a.name}</div>
                </div>
                <span className="am-pill" style={{ color: m.c, background: 'transparent' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: m.c, boxShadow: `0 0 7px ${m.c}` }} />
                </span>
                <AmChev />
              </div>
            );
          })}
        </div>
      </div>

      <div className="am-group" style={{ '--i': 3 }}>
        <div className="am-ghead">{t('admin.overview.moreTitle')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { k: 'users', ico: 'person', label: t('admin.nav.users'), sub: t('admin.overview.moreSub.users') },
            { k: 'support', ico: 'chat', label: t('admin.nav.support'), sub: t('admin.overview.moreSub.support') },
          ].map(({ k, ico, label, sub }) => (
            <button key={k} className="am-tile am-press" onClick={() => onSub(k)} style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 16, padding: '16px 14px', cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(225,18,23,0.1)', border: '1px solid var(--line-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-bright)' }}>
                <AmIcon name={ico} size={18} stroke={2} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>{label}</div>
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>{sub}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MRegsScreen({ accounts, filter, setFilter, query, setQuery, counts, onOpen }) {
  const { t } = useTranslation();
  const segs = [
    { k: 'all', l: t('admin.regs.filterAll') },
    { k: 'pending', l: t('admin.regs.filterPending') },
    { k: 'verified', l: t('admin.regs.filterVerified') },
    { k: 'rejected', l: t('admin.regs.filterRejected') },
  ];
  const filtered = accounts.filter(a =>
    (filter === 'all' || (filter === 'pending' ? (a.status === 'submitted' || a.status === 'moderation') : a.status === filter)) &&
    (a.name + a.nick + a.email).toLowerCase().includes(query.toLowerCase()));
  return (
    <div>
      <div style={{ animation: 'amUp .45s var(--ease-out) both' }}>
        <AmSeg value={filter} options={segs} onChange={setFilter} />
        <div className="am-search">
          <span style={{ color: 'var(--fg-faint)', display: 'flex' }}><AmIcon name="search" size={17} stroke={2} /></span>
          <input placeholder={t('admin.regs.searchPlaceholder')} value={query} onChange={e => setQuery(e.target.value)} />
          {query && <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', color: 'var(--fg-faint)', cursor: 'pointer', display: 'flex', padding: 0 }}><AmIcon name="xcircle" size={18} /></button>}
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="am-empty"><AmIcon name="search" size={30} stroke={1.6} /><span style={{ fontSize: 14 }}>{t('admin.mobile.notFound')}</span></div>
      ) : (
        <div className="am-inset am-stagger">
          {filtered.map((a, i) => {
            const m = amMobileStatus(a.status);
            const tier = premierTier(a.rating);
            return (
              <div key={a.id} className="am-row tap" style={{ '--i': i }} onClick={() => onOpen(a)}>
                <AmAvatar name={a.name} rating={a.rating} />
                <div className="am-row-main">
                  <div className="am-row-t">{a.name}</div>
                  <div className="am-row-s"><span className="mono">@{a.nick}</span>{a.rating ? <span style={{ color: tier.c, fontWeight: 600 }}> · {a.rating.toLocaleString('ru-RU')}</span> : null}</div>
                </div>
                <span className="am-pill" style={{ color: m.c, background: `${m.c}1c`, border: `1px solid ${m.c}33` }}>{t(m.short)}</span>
                <AmChev />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MTeamsScreen({ teams, onModerate, onDelete }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null);
  if (!teams.length) return <div className="am-empty"><AmIcon name="users" size={30} stroke={1.6} /><span style={{ fontSize: 14 }}>{t('admin.mobile.teamEmpty')}</span></div>;

  const moderate = async (tm, action) => {
    let reason = null;
    if (action === 'reject') {
      reason = window.prompt(t('admin.teamModal.rejectReasonPrompt'));
      if (reason === null) return;
    }
    setBusy(tm.id);
    try { await onModerate(tm.id, action, reason); }
    finally { setBusy(null); }
  };

  const remove = async (tm) => {
    if (!window.confirm(t('admin.teamModal.deleteConfirm', { name: tm.name }))) return;
    setBusy(tm.id);
    try { await onDelete(tm.id); }
    finally { setBusy(null); }
  };

  return (
    <div className="am-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {teams.map((tm, i) => {
        const onModeration = tm.rawStatus === 'moderation';
        const pillColor = tm.rawStatus === 'full' ? 'var(--ok)' : onModeration ? 'var(--info)' : tm.rawStatus === 'recruiting' ? 'var(--ok)' : 'var(--warn)';
        return (
        <div key={tm.id} className="am-tile am-press" style={{ '--i': i }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 14 }}>
            <div className="am-tagtile">{(tm.tag || '??').slice(0, 3)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>{tm.name}</div>
              <div className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>{tm.captain}</div>
            </div>
            <span className="am-pill" style={{
              color: pillColor,
              background: `${pillColor}22`,
              border: `1px solid ${pillColor}4d`,
            }}>{tm.status}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="am-meta">
              <div><div className="am-meta-l">{t('admin.mobile.roster')}</div><div className="am-meta-v">{tm.players}/5</div></div>
              <div><div className="am-meta-l">{t('admin.mobile.avgRating')}</div><div className="am-meta-v">{tm.avg ? tm.avg.toLocaleString('ru-RU') : '—'}</div></div>
            </div>
            {tm.avg ? <PremierBadge rating={tm.avg} size="sm" /> : null}
          </div>
          {onModeration && (
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <Btn variant="outline" size="sm" full disabled={busy === tm.id} onClick={() => moderate(tm, 'reject')} style={{ color: 'var(--danger)', boxShadow: 'inset 0 0 0 1.5px var(--danger)' }}>{t('admin.modal.rejectTeam')}</Btn>
              <Btn variant="gold" size="sm" full disabled={busy === tm.id} onClick={() => moderate(tm, 'approve')}>{t('admin.modal.approveTeam')}</Btn>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <Btn variant="ghost" size="sm" full disabled={busy === tm.id} onClick={() => remove(tm)} style={{ color: 'var(--danger)' }}>{t('admin.teamModal.deleteBtn')}</Btn>
          </div>
        </div>
        );
      })}
    </div>
  );
}

function MTournamentSheet({ tournament, teams, onClose, onUpdate }) {
  const { t } = useTranslation();
  const [view, setView] = useState('overview');
  const [page, setPage] = useState(0);
  const [regOpen, setRegOpen] = useState(!!tournament?.reg_open);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!tournament) return;
    setView('overview');
    setPage(0);
    setRegOpen(!!tournament.reg_open);
    setForm({ title: tournament.title || '', date: tournament.date || '', prize: tournament.prize || '', fee: tournament.fee || '', format: tournament.format || '', telegram: tournament.telegram || '' });
  }, [tournament]);

  if (!tournament) return null;
  const tournamentTeams = teams.filter(tm => tm.tournament_id === tournament.id);
  const pageSize = 3;
  const pages = Math.max(1, Math.ceil(tournamentTeams.length / pageSize));
  const visibleTeams = tournamentTeams.slice(page * pageSize, page * pageSize + pageSize);
  const fill = Math.min(100, (tournament.teams || 0) / (tournament.teams_cap || 64) * 100);
  const tabs = [
    { k: 'overview', ico: 'grid', l: t('admin.tournamentModal.innerOverview') },
    { k: 'teams', ico: 'users', l: t('admin.tournamentModal.innerTeams') },
    { k: 'bracket', ico: 'bracket', l: t('admin.tournamentModal.innerBracket') },
    { k: 'edit', ico: 'edit', l: t('admin.tournamentModal.editBtn') },
  ];

  const toggleRegistration = async () => {
    const next = !regOpen;
    setRegOpen(next);
    try { await onUpdate(tournament.id, { reg_open: next }); }
    catch (_) { setRegOpen(!next); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await onUpdate(tournament.id, { ...form, reg_open: regOpen });
      setView('overview');
    } catch (_) {
      // The shared store already shows a user-facing error toast.
    } finally { setSaving(false); }
  };

  return (
    <div className="am-tourn-ov" onClick={onClose}>
      <section className="am-tourn-sheet" onClick={e => e.stopPropagation()}>
        <header className="am-tourn-head">
          <div className="am-tourn-mark"><AmIcon name="trophy" size={22} stroke={1.8} /></div>
          <div className="am-tourn-heading">
            <div className="am-tourn-title">{tournament.title || tournament.name}</div>
            <div className="am-tourn-caption">{tournament.date || '—'} · {tournament.prize || tournament.statusLabel}</div>
          </div>
          <button className="am-tourn-close" onClick={onClose} aria-label={t('admin.modal.closeBtn')}><AmIcon name="xcircle" size={22} /></button>
        </header>

        <nav className="am-tourn-tabs" aria-label={t('admin.mobile.tournamentSections')}>
          {tabs.map(item => (
            <button key={item.k} className={'am-tourn-tab' + (view === item.k ? ' on' : '')} onClick={() => setView(item.k)}>
              <span className="am-tourn-tab-ico"><AmIcon name={item.ico} size={17} stroke={2} /></span>
              <span>{item.l}</span>
            </button>
          ))}
        </nav>

        <div className="am-tourn-content" key={view}>
          {view === 'overview' && (
            <div className="am-tourn-panel">
              <div className="am-tourn-stats">
                {[
                  [t('admin.tournamentModal.statTeams'), tournament.teams || 0, 'accent'],
                  [t('admin.tournamentModal.statPlayers'), tournament.players || 0, ''],
                  [t('admin.tournamentModal.statLimit'), tournament.teams_cap || '—', ''],
                  [t('admin.tournamentModal.statReg'), regOpen ? t('admin.tournamentModal.regOpen') : t('admin.tournamentModal.regClosed'), regOpen ? 'ok' : 'warn'],
                ].map(([label, value, tone]) => (
                  <div className={'am-tourn-stat ' + tone} key={label}>
                    <span>{label}</span><strong>{value}</strong>
                  </div>
                ))}
              </div>

              <div className="am-tourn-block">
                <div className="am-tourn-block-row"><span>{t('admin.tournamentModal.fillLabel')}</span><b>{tournament.teams || 0}/{tournament.teams_cap || 64}</b></div>
                <div className="am-tourn-progress"><i style={{ width: `${fill}%` }} /></div>
              </div>

              <button className="am-tourn-switch" onClick={toggleRegistration}>
                <span><b>{t('admin.tournamentModal.regToggle')}</b><small>{regOpen ? t('admin.mobile.regCanApply') : t('admin.mobile.regPaused')}</small></span>
                <span className={'am-tg' + (regOpen ? ' on' : '')} aria-hidden="true"><span className="am-tg-k" /></span>
              </button>

              <div className="am-tourn-actions">
                <button className="am-btn ghost" onClick={onClose}><AmIcon name="back" size={18} />{t('admin.mobile.toTournaments')}</button>
                <button className="am-btn primary" onClick={() => setView('edit')}><AmIcon name="edit" size={18} />{t('admin.tournamentModal.editBtn')}</button>
              </div>
            </div>
          )}

          {view === 'teams' && (
            <div className="am-tourn-panel">
              <div className="am-tourn-section-title"><span>{t('admin.tournamentModal.innerTeams')}</span><b>{tournamentTeams.length}</b></div>
              <div className="am-tourn-team-list">
                {visibleTeams.length ? visibleTeams.map((tm, i) => (
                  <div className="am-tourn-team" key={tm.id}>
                    <span className="am-tourn-team-tag">{(tm.tag || '??').slice(0, 3)}</span>
                    <span><b>{tm.name}</b><small>{tm.captain} · {tm.players}/5</small></span>
                    <PremierBadge rating={tm.avg} size="sm" />
                  </div>
                )) : <div className="am-empty am-tourn-empty"><AmIcon name="users" size={30} /><span>{t('admin.mobile.teamEmpty')}</span></div>}
              </div>
              {pages > 1 && (
                <div className="am-tourn-pager">
                  <button disabled={page === 0} onClick={() => setPage(p => p - 1)}><AmIcon name="back" size={18} /></button>
                  <span>{page + 1} / {pages}</span>
                  <button disabled={page === pages - 1} onClick={() => setPage(p => p + 1)}><AmIcon name="chevron" size={18} /></button>
                </div>
              )}
              <button className="am-btn ghost am-tourn-back" onClick={() => setView('overview')}><AmIcon name="back" size={18} />{t('admin.tournamentModal.innerOverview')}</button>
            </div>
          )}

          {view === 'bracket' && (
            <div className="am-tourn-panel">
              <div className="am-tourn-bracket">
                <span className="am-tourn-bracket-ico"><AmIcon name="bracket" size={34} stroke={1.5} /></span>
                <b>{t('admin.tournamentModal.bracketEmpty')}</b>
                <small>{t('admin.tournamentModal.bracketType')}</small>
              </div>
              <button className="am-btn ghost am-tourn-back" onClick={() => setView('overview')}><AmIcon name="back" size={18} />{t('admin.tournamentModal.innerOverview')}</button>
            </div>
          )}

          {view === 'edit' && (
            <div className="am-tourn-panel">
              <div className="am-tourn-form">
                {[
                  [t('admin.tournamentModal.fieldName'), 'title'],
                  [t('admin.tournamentModal.fieldDate'), 'date'],
                  [t('admin.tournamentModal.fieldPrize'), 'prize'],
                  [t('admin.tournamentModal.fieldFee'), 'fee'],
                  [t('admin.tournamentModal.fieldFormat'), 'format'],
                  ['Telegram', 'telegram'],
                ].map(([label, key]) => (
                  <label className={'am-tourn-field' + (key === 'title' ? ' wide' : '')} key={key}>
                    <span>{label}</span>
                    <input value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                  </label>
                ))}
              </div>
              <div className="am-tourn-actions">
                <button className="am-btn ghost" onClick={() => setView('overview')}>{t('admin.tournamentModal.cancelBtn')}</button>
                <button className="am-btn primary" disabled={saving} onClick={save}>{saving ? <Spinner size={16} /> : <AmIcon name="check" size={18} stroke={2.6} />}{saving ? t('admin.tournamentModal.saving') : t('admin.tournamentModal.saveBtn')}</button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function MTournamentsScreen({ tournaments, teams, onUpdate }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const updateSelected = async (id, data) => {
    const updated = await onUpdate(id, data);
    setSelected(current => current?.id === id ? { ...current, ...data, ...(updated || {}) } : current);
    return updated;
  };
  if (!tournaments.length) return <div className="am-empty"><AmIcon name="trophy" size={30} stroke={1.6} /><span style={{ fontSize: 14 }}>{t('admin.mobile.tournEmpty')}</span></div>;
  return (
    <>
      <div className="am-stagger am-tournament-list">
        {tournaments.map((tn, i) => (
          <article key={tn.id} className="am-tile am-tournament-card" style={{ '--i': i }}>
            <button className="am-tournament-main" onClick={() => setSelected(tn)}>
              <span className="am-tagtile"><AmIcon name="trophy" size={21} stroke={1.7} /></span>
              <span className="am-tournament-copy">
                <b>{tn.title || tn.name}</b>
                <small>{tn.date || '—'} · {tn.prize || tn.statusLabel}</small>
              </span>
              <span className="am-pill" style={{
                color: tn.status === 'registration' ? 'var(--accent-bright)' : 'var(--fg-muted)',
                background: tn.status === 'registration' ? 'rgba(225,18,23,0.14)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${tn.status === 'registration' ? 'var(--line-accent)' : 'var(--line)'}`,
              }}>{tn.statusLabel}</span>
            </button>
            <div className="am-tournament-metrics">
              <span><b>{tn.teams || 0}</b><small>{t('admin.mobile.teamsLabel')}</small></span>
              <span><b>{tn.players || 0}</b><small>{t('admin.mobile.playersLabel')}</small></span>
            </div>
            <button className="am-tournament-manage" onClick={() => setSelected(tn)}>
              <AmIcon name="edit" size={18} />{t('admin.tournaments.manage')}<AmChev />
            </button>
          </article>
        ))}
      </div>
      {selected && <MTournamentSheet tournament={selected} teams={teams} onUpdate={updateSelected} onClose={() => setSelected(null)} />}
    </>
  );
}

function MUsersScreen({ users }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const filtered = users.filter(u =>
    !q || ((u.nickname || '') + ' ' + (u.email || '')).toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div>
      <div className="am-search" style={{ animation: 'amUp .35s var(--ease-out) both' }}>
        <span style={{ color: 'var(--fg-faint)', display: 'flex' }}><AmIcon name="search" size={17} stroke={2} /></span>
        <input placeholder={t('admin.mobile.searchNick')} value={q} onChange={e => setQ(e.target.value)} />
        {q && <button onClick={() => setQ('')} style={{ background: 'none', border: 'none', color: 'var(--fg-faint)', cursor: 'pointer', display: 'flex', padding: 0 }}><AmIcon name="xcircle" size={18} /></button>}
      </div>
      {filtered.length === 0 ? (
        <div className="am-empty"><AmIcon name="person" size={30} stroke={1.6} /><span style={{ fontSize: 14 }}>{t('admin.mobile.notFound')}</span></div>
      ) : (
        <div className="am-inset am-stagger">
          {filtered.map((u, i) => {
            const isCaptain = u.user_type === 'captain';
            return (
              <div key={u.id} className="am-row" style={{ '--i': i }}>
                <div className="am-av" style={{ width: 36, height: 36, fontSize: 13, background: isCaptain ? 'linear-gradient(140deg, var(--gold)33, var(--surface-3))' : 'var(--surface-3)', border: `1px solid ${isCaptain ? 'var(--gold)' : 'var(--line)'}` }}>
                  {(u.nickname || 'PL').slice(0, 2).toUpperCase()}
                </div>
                <div className="am-row-main">
                  <div className="am-row-t">{u.nickname || '—'}</div>
                  <div className="am-row-s">{u.email}</div>
                </div>
                <span className="am-pill" style={{ color: isCaptain ? 'var(--gold)' : 'var(--fg-muted)', background: isCaptain ? 'rgba(245,185,69,0.12)' : 'var(--surface-3)', border: `1px solid ${isCaptain ? 'rgba(245,185,69,0.3)' : 'var(--line)'}`, fontSize: 10 }}>
                  {isCaptain ? t('admin.users.roleCaptain') : t('admin.users.rolePlayer')}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MSupportScreen({ tickets, markTicketRead, sendNotification }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [replyLoading, setReplyLoading] = useState({});
  const [replySent, setReplySent] = useState({});

  const handleReply = async (tk) => {
    const msg = (replyTexts[tk.id] || '').trim();
    if (!msg || !tk.user_id) return;
    setReplyLoading(p => ({ ...p, [tk.id]: true }));
    try {
      await sendNotification({ to: tk.user_id, type: 'info', title: t('admin.support.replyTitle'), message: msg });
      setReplyTexts(p => ({ ...p, [tk.id]: '' }));
      setReplySent(p => ({ ...p, [tk.id]: true }));
      if (tk.status === 'new' && markTicketRead) markTicketRead(tk.id);
      setTimeout(() => setReplySent(p => ({ ...p, [tk.id]: false })), 2500);
    } catch (_) {}
    setReplyLoading(p => ({ ...p, [tk.id]: false }));
  };

  const segs = [
    { k: 'all', l: t('admin.filter.all') },
    { k: 'new', l: t('admin.support.filterNew') },
    { k: 'read', l: t('admin.support.filterRead') },
  ];
  const filtered = tickets.filter(tk => filter === 'all' || tk.status === filter);
  if (!tickets.length) return (
    <div className="am-empty"><AmIcon name="chat" size={30} stroke={1.6} /><span style={{ fontSize: 14 }}>{t('admin.support.empty')}</span></div>
  );
  return (
    <div>
      <div style={{ animation: 'amUp .35s var(--ease-out) both' }}>
        <AmSeg value={filter} options={segs} onChange={setFilter} />
      </div>
      {filtered.length === 0 ? (
        <div className="am-empty"><AmIcon name="chat" size={30} stroke={1.6} /><span style={{ fontSize: 14 }}>{t('admin.mobile.notFound')}</span></div>
      ) : (
        <div className="am-stagger" style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((tk, i) => {
            const isNew = tk.status === 'new';
            const isOpen = expanded === tk.id;
            return (
              <div key={tk.id} className="am-tile" style={{ '--i': i, padding: 0, overflow: 'hidden' }}>
                <div
                  className="am-row tap"
                  style={{ padding: '12px 14px', alignItems: 'flex-start', gap: 10 }}
                  onClick={() => setExpanded(isOpen ? null : tk.id)}
                >
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: isNew ? 'var(--info)' : 'var(--fg-faint)', flexShrink: 0, marginTop: 5 }} />
                  <div className="am-row-main" style={{ gap: 3 }}>
                    <div className="am-row-t" style={{ fontSize: 13 }}>{tk.name || tk.user_email || `ID ${tk.user_id}`}</div>
                    <div className="am-row-s" style={{ whiteSpace: isOpen ? 'pre-wrap' : 'nowrap', overflow: isOpen ? 'visible' : 'hidden', textOverflow: 'ellipsis' }}>{tk.message}</div>
                  </div>
                  {isNew && <span className="am-pill" style={{ color: 'var(--info)', background: 'rgba(88,166,255,0.12)', border: '1px solid rgba(88,166,255,0.3)', fontSize: 9, flexShrink: 0 }}>{t('admin.support.badgeNew')}</span>}
                </div>
                {isOpen && (
                  <div style={{ borderTop: '1px solid var(--line)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {isNew && markTicketRead && (
                      <button onClick={() => markTicketRead(tk.id)} style={{ alignSelf: 'flex-start', background: 'rgba(88,166,255,0.1)', border: '1px solid rgba(88,166,255,0.35)', borderRadius: 8, padding: '5px 12px', fontSize: 12, color: 'var(--info)', cursor: 'pointer' }}>
                        ✓ {t('admin.support.markRead')}
                      </button>
                    )}
                    {tk.user_id ? (
                      <>
                        <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', color: 'var(--fg-muted)' }}>{t('admin.support.replyLabel')}</div>
                        <textarea
                          value={replyTexts[tk.id] || ''}
                          onChange={e => setReplyTexts(p => ({ ...p, [tk.id]: e.target.value }))}
                          placeholder={t('admin.support.replyPlaceholder')}
                          rows={3}
                          style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '9px 12px', color: 'var(--fg)', fontSize: 13, fontFamily: 'var(--font-body)', resize: 'none', outline: 'none', boxSizing: 'border-box' }}
                        />
                        <button
                          disabled={!replyTexts[tk.id]?.trim() || replyLoading[tk.id]}
                          onClick={() => handleReply(tk)}
                          className="am-btn"
                          style={{
                            background: replySent[tk.id] ? 'var(--ok)' : replyTexts[tk.id]?.trim() ? 'var(--accent)' : 'var(--surface-3)',
                            color: (replySent[tk.id] || replyTexts[tk.id]?.trim()) ? '#fff' : 'var(--fg-faint)',
                            boxShadow: (replyTexts[tk.id]?.trim() && !replySent[tk.id]) ? '0 0 18px -4px var(--accent-glow)' : 'none',
                          }}
                        >
                          {replySent[tk.id] ? `✓ ${t('admin.support.replySent')}` : replyLoading[tk.id] ? '...' : t('admin.support.sendReply')}
                        </button>
                      </>
                    ) : tk.email ? (
                      <a href={`mailto:${tk.email}`} style={{ fontSize: 13, color: 'var(--fg-muted)', textDecoration: 'none' }}>✉ {tk.email}</a>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const AM_NOTIFY_TYPES = [
  { k: 'info', c: '#58a6ff' },
  { k: 'success', c: '#56d364' },
  { k: 'warning', c: '#f5b942' },
  { k: 'alert', c: '#ff3b3b' },
];

function MNotifsScreen({ users, sendNotification, notifications }) {
  const { t } = useTranslation();
  const [toMode, setToMode] = useState('all');
  const [userQ, setUserQ] = useState('');
  const [selUserId, setSelUserId] = useState('');
  const [type, setType] = useState('info');
  const [title, setTitle] = useState('');
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const searchQ = userQ.replace(/^@/, '').trim();
  const filteredUsers = users.filter(u => searchQ.length > 0 &&
    ((u.nickname || '') + ' ' + (u.email || '')).toLowerCase().includes(searchQ.toLowerCase())).slice(0, 6);
  // typeMeta used in preview block below
  const canSend = title.trim() && msg.trim() && (toMode === 'all' || selUserId);

  const submit = async () => {
    if (!canSend || sending) return;
    setSending(true);
    try {
      await sendNotification({ to: toMode === 'all' ? 'all' : selUserId, type, title: title.trim(), message: msg.trim() });
      setSent(true); setTimeout(() => setSent(false), 2000);
      setTitle(''); setMsg(''); setUserQ(''); setSelUserId('');
    } catch (_) {}
    setSending(false);
  };

  const sentList = [...notifications].reverse().slice(0, 20);

  return (
    <div className="col gap-4">
      <div className="am-group">
        <div className="am-ghead">{t('admin.notifs.recipient')}</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {[[`all`, t('admin.mobile.recipientAll')], [`user`, t('admin.mobile.recipientUser')]].map(([k, l]) => (
            <button key={k} onClick={() => setToMode(k)} style={{
              flex: 1, padding: '10px', borderRadius: 12, cursor: 'pointer',
              border: `1px solid ${toMode === k ? 'var(--accent)' : 'var(--line)'}`,
              background: toMode === k ? 'rgba(225,18,23,0.1)' : 'var(--surface-2)',
              color: toMode === k ? 'var(--accent-bright)' : 'var(--fg-muted)',
              fontWeight: 700, fontSize: 13, transition: 'all .18s',
            }}>{l}</button>
          ))}
        </div>
        {toMode === 'user' && (
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <div className="am-search" style={{ marginBottom: 0 }}>
              <span style={{ color: 'var(--fg-faint)', display: 'flex' }}><AmIcon name="search" size={16} /></span>
              <input placeholder={t('admin.mobile.searchNick')} value={userQ} onChange={e => { setUserQ(e.target.value); setSelUserId(''); }} />
            </div>
            {userQ && filteredUsers.length > 0 && !selUserId && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 13, overflow: 'hidden' }}>
                {filteredUsers.map(u => (
                  <div key={u.id} onClick={() => { setSelUserId(String(u.id)); setUserQ(u.nickname || u.email || `ID ${u.id}`); }}
                    style={{ padding: '11px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--line)' }}>
                    <span style={{ fontSize: 14 }}>{u.nickname || u.email}</span>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>ID {u.id}</span>
                  </div>
                ))}
              </div>
            )}
            {selUserId && <div className="mono" style={{ fontSize: 11, color: 'var(--ok)', marginTop: 6, letterSpacing: '0.08em' }}>{t('admin.notifs.selectedUser', { id: selUserId })}</div>}
          </div>
        )}
      </div>

      <div className="am-group" style={{ marginTop: -10 }}>
        <div className="am-ghead">{t('admin.notifs.typeLabel')}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {AM_NOTIFY_TYPES.map(nt => (
            <button key={nt.k} onClick={() => setType(nt.k)} style={{
              padding: '7px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 700,
              border: `1px solid ${type === nt.k ? nt.c : 'var(--line)'}`,
              background: type === nt.k ? `${nt.c}1a` : 'transparent',
              color: type === nt.k ? nt.c : 'var(--fg-muted)', transition: 'all .18s',
            }}>{t(`admin.notifs.types.${nt.k}`)}</button>
          ))}
        </div>
      </div>

      <div className="am-field" style={{ marginTop: -10 }}>
        <div className="am-field-l">{t('admin.notifs.titleLabel')}</div>
        <input value={title} maxLength={80} onChange={e => setTitle(e.target.value)} placeholder={t('admin.notifs.titlePlaceholder')}
          style={{ width: '100%', background: 'none', border: 'none', outline: 'none', color: 'var(--fg)', fontSize: 15, marginTop: 6, fontFamily: 'var(--font-body)' }} />
        <div className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', textAlign: 'right', marginTop: 2 }}>{title.length}/80</div>
      </div>

      <div className="am-field">
        <div className="am-field-l">{t('admin.notifs.msgLabel')}</div>
        <textarea value={msg} maxLength={400} rows={3} onChange={e => setMsg(e.target.value)} placeholder={t('admin.notifs.msgPlaceholder')}
          style={{ width: '100%', background: 'none', border: 'none', outline: 'none', color: 'var(--fg)', fontSize: 14, marginTop: 6, fontFamily: 'var(--font-body)', resize: 'vertical', lineHeight: 1.5 }} />
        <div className="mono" style={{ fontSize: 9, color: 'var(--fg-faint)', textAlign: 'right', marginTop: 2 }}>{msg.length}/400</div>
      </div>

      <button disabled={!canSend || sending} onClick={submit} className="am-btn" style={{
        background: sent ? 'var(--ok)' : canSend ? 'var(--accent)' : 'var(--surface-3)',
        color: (canSend || sent) ? '#fff' : 'var(--fg-faint)',
        boxShadow: (canSend && !sent) ? '0 0 20px -4px var(--accent-glow)' : 'none',
        width: '100%',
      }}>
        {sent ? <><AmIcon name="check" size={17} stroke={3} /> {t('admin.notifs.sent')}</> : <><AmIcon name="send" size={16} /> {toMode === 'all' ? t('admin.notifs.sendAll') : t('admin.notifs.sendUser')}</>}
      </button>

      {sentList.length > 0 && (
        <div className="am-group" style={{ marginTop: 8 }}>
          <div className="am-ghead">{t('admin.notifs.history')}</div>
          <div className="am-inset">
            {sentList.map((n, i) => {
              const meta = AM_NOTIFY_TYPES.find(t => t.k === n.type) || AM_NOTIFY_TYPES[0];
              return (
                <div key={n.id || i} className="am-row" style={{ alignItems: 'flex-start' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: meta.c, marginTop: 6, flexShrink: 0 }} />
                  <div className="am-row-main">
                    <div className="am-row-t" style={{ fontSize: 13 }}>{n.title}</div>
                    <div className="am-row-s" style={{ fontSize: 11.5 }}>{n.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MMoreScreen({ onOpen, onHome, supportNewCount = 0 }) {
  const { t } = useTranslation();
  const services = [
    { k: 'notifs', ico: 'bell', label: t('admin.tabs.notifs'), sub: t('admin.mobile.serviceNotifs'), tone: '#E11217' },
    { k: 'users', ico: 'person', label: t('admin.nav.users'), sub: t('admin.mobile.serviceUsers'), tone: '#58A6FF' },
    { k: 'support', ico: 'chat', label: t('admin.nav.support'), sub: t('admin.mobile.serviceSupport'), tone: '#A78BFA', badge: supportNewCount },
    { k: 'settings', ico: 'gear', label: t('admin.nav.settings'), sub: t('admin.mobile.serviceSettings'), tone: '#4ADE80' },
    { k: 'home', ico: 'home', label: t('admin.mobile.toSite'), sub: t('admin.mobile.serviceSite'), tone: '#F2CC5E', home: true },
  ];
  return (
    <div className="am-stagger">
      <div className="am-group" style={{ '--i': 0 }}>
        <div className="am-ghead">{t('admin.mobile.services')}</div>
        <div className="am-services">
          {services.map(service => (
            <button key={service.k} className="am-service" onClick={() => service.home ? onHome() : onOpen(service.k)}>
              <span className="am-service-ico" style={{ '--service': service.tone }}>
                <AmIcon name={service.ico} size={19} stroke={1.9} />
                {!!service.badge && <i>{service.badge}</i>}
              </span>
              <b>{service.label}</b>
              <small>{service.sub}</small>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MSettingsScreen({ go }) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({ reg: true, steam: true, doc: true, tg: false, push: true });
  const toggle = k => setSettings(s => ({ ...s, [k]: !s[k] }));
  const groups = [
    { label: t('admin.settings.groups.tournament'), items: [
      { k: 'reg', l: t('admin.settings.items.reg'), s: t('admin.settings.items.regSub') },
      { k: 'steam', l: t('admin.settings.items.steam'), s: t('admin.settings.items.steamSub') },
      { k: 'doc', l: t('admin.settings.items.doc'), s: t('admin.settings.items.docSub') },
    ]},
    { label: t('admin.settings.groups.notifications'), items: [
      { k: 'tg', l: t('admin.settings.items.tg'), s: t('admin.settings.items.tgSub') },
      { k: 'push', l: t('admin.settings.items.push'), s: t('admin.settings.items.pushSub') },
    ]},
  ];
  return (
    <div className="am-stagger">
      {groups.map((grp, gi) => (
        <div key={grp.label} className="am-group" style={{ '--i': gi }}>
          <div className="am-ghead">{grp.label}</div>
          <div className="am-inset">
            {grp.items.map(it => (
              <div key={it.k} className="am-row">
                <div className="am-row-main">
                  <div className="am-row-t">{it.l}</div>
                  <div className="am-row-s">{it.s}</div>
                </div>
                <AmToggle on={!!settings[it.k]} onClick={() => toggle(it.k)} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="am-group" style={{ '--i': 2 }}>
        <div className="am-ghead">{t('admin.settings.groups.account')}</div>
        <div className="am-inset">
          <div className="am-row tap" onClick={() => go('home')}>
            <div className="am-row-main"><div className="am-row-t">{t('admin.settings.backToSite')}</div></div>
            <AmChev />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '8px 0 4px', opacity: 0.5, '--i': 3 }}>
        <Logo size={28} />
        <span className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', color: 'var(--fg-faint)' }}>CS2 OPEN · СКО · v2.6</span>
      </div>
    </div>
  );
}

/* ---- RegSheet — bottom sheet detail ---- */
function MRegSheet({ acc, onClose, onAction }) {
  const { t } = useTranslation();
  const [closing, setClosing] = useState(false);
  const close = useCallback(() => { setClosing(true); setTimeout(onClose, 240); }, [onClose]);
  if (!acc) return null;
  const m = amMobileStatus(acc.status);
  const tier = premierTier(acc.rating);
  const initials = (acc.name || 'PL').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const act = (st) => { onAction(acc.id, st); close(); };
  const fields = [
    [t('admin.modal.fio'), acc.name], [t('admin.modal.nickname'), '@' + acc.nick], [t('admin.modal.email'), acc.email],
    [t('admin.modal.steam'), acc.steam], [t('admin.modal.edu'), acc.edu],
    [t('admin.modal.premier'), acc.rating ? acc.rating.toLocaleString('ru-RU') : '—'],
    [t('admin.modal.dateApplied'), acc.joined],
  ];
  return (
    <div className="am-sheet-ov" style={closing ? { animation: 'amOverlay .24s ease reverse both' } : undefined} onClick={close}>
      <div className="am-sheet" style={closing ? { animation: 'amSheetUp .26s var(--ease-out) reverse both' } : undefined} onClick={e => e.stopPropagation()}>
        <div className="am-sheet-handle" />
        <div className="am-sheet-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 18 }}>
            <div className="am-av" style={{ width: 50, height: 50, fontSize: 16, background: `linear-gradient(140deg, ${tier.c}33, var(--surface-3))`, border: `1px solid ${tier.c}` }}>{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>{acc.nick}</div>
              <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 1 }}>{acc.name}</div>
            </div>
            <span className="am-pill" style={{ color: m.c, background: `${m.c}1c`, border: `1px solid ${m.c}33` }}>{t(m.short)}</span>
          </div>

          <div className="am-ghead" style={{ padding: '0 2px 8px' }}>{t('admin.sheet.document')}</div>
          <div className="am-doc" style={!acc.doc ? { background: 'var(--surface)', borderColor: 'var(--danger)' } : undefined}>
            {acc.doc ? (
              <>
                <div style={{ width: 110, height: 70, borderRadius: 8, background: 'linear-gradient(135deg, var(--surface-3), var(--elevated))', border: '1px solid var(--line-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 7, top: 7, width: 20, height: 20, borderRadius: '50%', background: 'var(--surface-2)', border: '1px solid var(--line)' }} />
                  <Logo size={26} />
                </div>
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{t('admin.sheet.docUploaded')}</span>
                {acc.docFile && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => openAuthedDoc(acc.docFile)} className="am-pill am-press" style={{ color: 'var(--fg-soft)', background: 'rgba(0,0,0,0.5)', border: '1px solid var(--line)', gap: 6, cursor: 'pointer' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> {t('admin.sheet.open')}
                    </button>
                    <button type="button" onClick={() => openAuthedDoc(acc.docFile, { download: true })} className="am-pill am-press" style={{ color: 'var(--fg-soft)', background: 'rgba(0,0,0,0.5)', border: '1px solid var(--line)', gap: 6, cursor: 'pointer' }}>
                      <AmIcon name="download" size={13} stroke={2} /> {t('admin.sheet.download')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: 'var(--danger)' }}>
                <AmIcon name="xcircle" size={26} stroke={2} />
                <span style={{ fontSize: 13 }}>{t('admin.sheet.docMissing')}</span>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginTop: 14 }}>
            {fields.map(([l, v], i) => (
              <div key={i} className="am-field" style={(l === t('admin.modal.fio') || l === t('admin.modal.email') || l === t('admin.modal.steam') || l === t('admin.modal.edu')) ? { gridColumn: '1 / -1' } : undefined}>
                <div className="am-field-l">{l}</div>
                <div className="am-field-v">{v || '—'}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="am-sheet-foot">
          <button className="am-btn ghost" onClick={() => act('rejected')}>{t('admin.sheet.reject')}</button>
          <button className="am-btn ok" onClick={() => act('verified')}>
            <AmIcon name="check" size={17} stroke={3} /> {t('admin.sheet.approve')}
          </button>
        </div>
      </div>
    </div>
  );
}

function amMobileStatus(s) {
  return s === 'verified'
    ? { c: 'var(--ok)', l: 'admin.sheet.statusVerified', short: 'admin.sheet.approvedShort' }
    : s === 'rejected'
      ? { c: 'var(--danger)', l: 'admin.sheet.statusRejected', short: 'admin.sheet.rejectedShort' }
      : { c: 'var(--warn)', l: 'admin.sheet.statusPending', short: 'admin.sheet.pendingShort' };
}

/* ---- AdminMobile shell ---- */
function AdminMobile({ go, user, accounts, counts, teams, tournaments, notifications, users, act, onModerateTeam, onDeleteTeam, onUpdateTournament, sendNotification, loading, tickets, markTicketRead, supportNewCount }) {
  const { t } = useTranslation();
  const AM_TABS = AM_TAB_KEYS.map(({ k, ico }) => ({
    k, ico,
    bar: t(`admin.tabs.${k === 'notifs' ? 'notifsShort' : k === 'settings' ? 'settingsShort' : k}`),
    title: t(`admin.tabs.${k}`),
  }));

  const [tabIdx, setTabIdx] = useState(0);
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [subscreen, setSubscreen] = useState(null);

  const scrollRef = useRef(null);
  const wrapRef = useRef(null);
  const navRef = useRef(null);
  const titleRef = useRef(null);

  const tab = AM_TABS[tabIdx];

  const subs = {
    overview: t('admin.mobile.subOverview'),
    regs: t('admin.mobile.awaitMod', { n: counts.pending, total: counts.all }),
    teams: t('admin.mobile.teamCountSub', { n: teams.length }),
    tournaments: t('admin.mobile.tournCountSub', { n: tournaments.length }),
    more: t('admin.mobile.subMore'),
  };

  const goTab = useCallback((i) => {
    setSubscreen(null);
    if (i === tabIdx) { scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setTabIdx(i);
  }, [tabIdx]);

  const goSub = useCallback((k) => {
    setSubscreen(k);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, []);

  const closeSub = useCallback(() => {
    setSubscreen(null);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    if (titleRef.current) titleRef.current.style.opacity = '0';
    if (navRef.current) navRef.current.classList.remove('scrolled');
  }, [tabIdx]);

  const onScroll = useCallback((e) => {
    const y = e.target.scrollTop;
    if (titleRef.current) {
      const o = Math.max(0, Math.min(1, (y - 24) / 28));
      titleRef.current.style.opacity = String(o);
      titleRef.current.style.transform = `translateY(${(1 - o) * 4}px)`;
    }
    if (navRef.current) navRef.current.classList.toggle('scrolled', y > 6);
  }, []);

  /* horizontal swipe */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let x0 = 0, y0 = 0, axis = null, dragging = false, curDx = 0;
    const setT = (v, op) => { if (wrapRef.current) { wrapRef.current.style.transform = v; if (op != null) wrapRef.current.style.opacity = String(op); } };
    const onStart = (ev) => { const t = ev.touches[0]; x0 = t.clientX; y0 = t.clientY; axis = null; dragging = false; curDx = 0; if (wrapRef.current) wrapRef.current.style.transition = 'none'; };
    const onMove = (ev) => {
      const t = ev.touches[0]; const dx = t.clientX - x0, dy = t.clientY - y0;
      if (!axis) { if (Math.abs(dx) > 9 || Math.abs(dy) > 9) axis = Math.abs(dx) > Math.abs(dy) * 1.3 ? 'x' : 'y'; else return; }
      if (axis !== 'x') return;
      dragging = true; ev.preventDefault();
      const edge = (tabIdx === 0 && dx > 0) || (tabIdx === AM_TAB_KEYS.length - 1 && dx < 0);
      let v = edge ? dx * 0.18 : dx * 0.55;
      v = Math.max(-110, Math.min(110, v)); curDx = dx;
      setT(`translateX(${v}px)`, 1 - Math.min(Math.abs(v) / 360, 0.22));
    };
    const onEnd = () => {
      if (wrapRef.current) wrapRef.current.style.transition = 'transform .26s var(--ease-out), opacity .26s';
      if (axis === 'x' && dragging && Math.abs(curDx) > 56) {
        const ni = tabIdx + (curDx < 0 ? 1 : -1);
        if (ni >= 0 && ni < AM_TAB_KEYS.length) { setT('translateX(0)', 1); setTabIdx(ni); return; }
      }
      setT('translateX(0)', 1);
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => { el.removeEventListener('touchstart', onStart); el.removeEventListener('touchmove', onMove); el.removeEventListener('touchend', onEnd); el.removeEventListener('touchcancel', onEnd); };
  }, [tabIdx]);

  const badgeFor = (k) => {
    if (k === 'regs' && counts.pending > 0) return <span className="am-badge">{counts.pending}</span>;
    if (k === 'more' && supportNewCount > 0) return <span className="am-badge">{supportNewCount}</span>;
    return null;
  };

  const nick = user?.nick || user?.nickname || 'AD';
  let screen = null;
  if (tab.k === 'overview') screen = <MOverviewScreen accounts={accounts} counts={counts} teams={teams} onOpen={setSel} onTab={(k) => goTab(AM_TABS.findIndex(tab => tab.k === k))} onSub={goSub} />;
  else if (tab.k === 'regs') screen = <MRegsScreen accounts={accounts} filter={filter} setFilter={setFilter} query={q} setQuery={setQ} counts={counts} onOpen={setSel} />;
  else if (tab.k === 'teams') screen = <MTeamsScreen teams={teams} onModerate={onModerateTeam} onDelete={onDeleteTeam} />;
  else if (tab.k === 'tournaments') screen = <MTournamentsScreen tournaments={tournaments} teams={teams} onUpdate={onUpdateTournament} />;
  else if (tab.k === 'more') screen = <MMoreScreen onOpen={goSub} onHome={() => go('home')} supportNewCount={supportNewCount} />;

  const subTitles = {
    users: t('admin.nav.users'),
    support: t('admin.nav.support'),
    notifs: t('admin.tabs.notifs'),
    settings: t('admin.nav.settings'),
  };
  const subDescriptions = {
    users: t('admin.mobile.subUsers'),
    support: t('admin.mobile.subSupport'),
    notifs: t('admin.mobile.subNotifs'),
    settings: t('admin.mobile.subSettings'),
  };
  const subTitle = subscreen ? subTitles[subscreen] : tab.title;

  return (
    <div className="am-root">
      {/* NAV BAR */}
      <div className="am-nav" ref={navRef}>
        <button className="am-nav-btn accent" onClick={subscreen ? closeSub : () => go('home')}>
          <AmIcon name="back" size={20} stroke={2.2} /> {subscreen ? t('admin.mobile.backSection') : t('admin.mobile.backBtn')}
        </button>
        <span className="am-nav-title" ref={titleRef}>{subTitle}</span>
        {!subscreen ? (
          <button className="am-nav-btn" onClick={() => goSub('settings')} style={{ padding: 0, width: 38 }} aria-label={t('admin.nav.settings')}>
            <span style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(140deg, var(--accent-deep), var(--surface-3))', border: '1px solid var(--line-accent)', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12, color: 'var(--fg)' }}>
              {nick.slice(0, 2).toUpperCase()}
            </span>
          </button>
        ) : <div style={{ width: 38 }} />}
      </div>

      {/* SCROLL BODY */}
      <div className="am-scroll" ref={scrollRef} onScroll={onScroll}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={28} /></div>
        ) : subscreen ? (
          <div key={subscreen} style={{ animation: 'amUp .35s var(--ease-out) both' }}>
            <div className="am-large">
              <h1 className="am-h1">{subTitles[subscreen]}</h1>
              <p className="am-sub">{subDescriptions[subscreen]}</p>
            </div>
            <div className="am-screen">
              {subscreen === 'users' && <MUsersScreen users={users} />}
              {subscreen === 'support' && <MSupportScreen tickets={tickets || []} markTicketRead={markTicketRead} sendNotification={sendNotification} />}
              {subscreen === 'notifs' && <MNotifsScreen users={users} sendNotification={sendNotification} notifications={notifications} />}
              {subscreen === 'settings' && <MSettingsScreen go={go} />}
            </div>
          </div>
        ) : (
          <div ref={wrapRef}>
            <div className="am-large" key={'L' + tab.k}>
              <h1 className="am-h1">{tab.title}</h1>
              <p className="am-sub">{subs[tab.k]}</p>
            </div>
            <div className="am-screen" key={'S' + tab.k}>{screen}</div>
          </div>
        )}
      </div>

      {/* BOTTOM DOCK — hidden when subscreen is active */}
      {!subscreen && (
        <div className="am-bar-dock am-bar-dock-pad">
          <div className="am-dock">
            <div className="am-ind" style={{ transform: `translateX(calc(${tabIdx} * 100%))` }} />
            {AM_TABS.map((tab, i) => (
              <button key={tab.k} className={'am-tab' + (i === tabIdx ? ' on' : '')} onClick={() => goTab(i)} aria-label={tab.title}>
                <span className="am-tab-ico">
                  <AmIcon name={tab.ico} size={22} stroke={i === tabIdx ? 2.1 : 1.85} />
                  {badgeFor(tab.k)}
                </span>
                <span className="am-tab-l">{tab.bar}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {sel && <MRegSheet acc={sel} onClose={() => setSel(null)} onAction={act} />}
    </div>
  );
}

const NAV = [
  { k: 'overview', icon: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z' },
  { k: 'regs', icon: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M9 2h6v4H9z' },
  { k: 'teams', icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm14 14v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8' },
  { k: 'users', icon: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' },
  { k: 'tournaments', icon: 'M6 9H4a2 2 0 0 1-2-2V5h4M18 9h2a2 2 0 0 0 2-2V5h-4M6 5h12v6a6 6 0 0 1-12 0zM9 19h6M10 22h4M12 17v2' },
  { k: 'notify', icon: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0' },
  { k: 'support', icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' },
  { k: 'settings', icon: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H1a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 7' },
];

export default function AdminPage() {
  const { t } = useTranslation();
  const { go, user, fetchApplications, fetchUserApplication, updateApplicationStatus, fetchTournaments, createTournament, updateTournament, fetchUsers, setUserRole, fetchSupportTickets, markSupportTicketRead, sendNotification, notifications, moderateTeam, adminDeleteTeam } = useAppStore();
  const [tab, setTab] = useState('overview');
  const [rawApps, setRawApps] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [users, setUsers] = useState([]);
  const [userQ, setUserQ] = useState('');
  const [roleLoading, setRoleLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [selTeam, setSelTeam] = useState(null);
  const [selTournament, setSelTournament] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [tickets, setTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [supportNewCount, setSupportNewCount] = useState(0);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [replyLoading, setReplyLoading] = useState({});
  const [replySent, setReplySent] = useState({});

  const handleTicketReply = useCallback(async (ticket) => {
    const msg = (replyTexts[ticket.id] || '').trim();
    if (!msg) return;
    setReplyLoading(p => ({ ...p, [ticket.id]: true }));
    try {
      await sendNotification({ to: ticket.user_id, type: 'info', title: t('admin.support.replyTitle'), message: msg });
      setReplyTexts(p => ({ ...p, [ticket.id]: '' }));
      setReplySent(p => ({ ...p, [ticket.id]: true }));
      if (ticket.status === 'new') {
        await markSupportTicketRead(ticket.id);
        setTickets(ts => ts.map(tk => tk.id === ticket.id ? { ...tk, status: 'read' } : tk));
        setSupportNewCount(c => Math.max(0, c - 1));
      }
      setTimeout(() => setReplySent(p => ({ ...p, [ticket.id]: false })), 2500);
    } catch (_) {}
    setReplyLoading(p => ({ ...p, [ticket.id]: false }));
  }, [replyTexts, sendNotification, markSupportTicketRead, t]);

  const isAdmin = user?.user_type === 'admin';
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const syncMobileLayout = event => setIsMobile(event.matches);
    setIsMobile(media.matches);
    media.addEventListener('change', syncMobileLayout);
    return () => media.removeEventListener('change', syncMobileLayout);
  }, []);

  const loadAll = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const [apps, rawTeams, tourns, rawUsers, supportData] = await Promise.all([
        fetchApplications(),
        useAppStore.getState().fetchTeams(),
        fetchTournaments(),
        fetchUsers(),
        fetchSupportTickets(),
      ]);
      setRawApps(apps);
      const appsByUser = {};
      apps.forEach(a => { appsByUser[a.user_id] = a; });
      setTeams(rawTeams.map(t => mapTeam(t, appsByUser)));
      setTournaments(tourns.map(mapTournament));
      setUsers(rawUsers);
      setTickets(supportData.tickets || []);
      setSupportNewCount(supportData.new_count || 0);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, fetchApplications, fetchTournaments, fetchSupportTickets]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const accounts = rawApps.map(mapApplication);
  const isPending = (s) => s === 'submitted' || s === 'moderation';

  const act = async (id, status) => {
    try {
      await updateApplicationStatus(id, status);
      setRawApps(a => a.map(x => x.id === id ? { ...x, status } : x));
    } catch (e) { /* тост уже показан */ }
  };

  const handleModerateTeam = async (teamId, action, reason) => {
    try {
      const updated = await moderateTeam(teamId, action, reason);
      const newStatus = updated?.status || (action === 'approve' ? 'full' : 'recruiting');
      setTeams(ts => ts.map(tm => tm.id === teamId
        ? { ...tm, rawStatus: newStatus, status: TEAM_STATUS[newStatus] || newStatus }
        : tm));
    } catch (e) { /* тост уже показан */ }
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      await adminDeleteTeam(teamId);
      setTeams(ts => ts.filter(tm => tm.id !== teamId));
    } catch (e) { /* тост уже показан */ }
  };

  const handleUpdateTournament = async (id, data) => {
    const updated = await updateTournament(id, data);
    setTournaments(list => list.map(item => item.id === id ? mapTournament({ ...item, ...data, ...(updated || {}) }) : item));
    return updated;
  };

  // Клик по игроку в составе команды → его карточка с документом и данными
  const openPlayer = async (p) => {
    if (!p?.userId) return;
    const app = await fetchUserApplication(p.userId, selTeam?.tournament_id);
    if (app) setSel(mapApplication(app));
  };

  const matchFilter = (a) => filter === 'all' || (filter === 'pending' ? isPending(a.status) : a.status === filter);
  const filtered = accounts.filter(a => matchFilter(a) && (a.name + a.nick + a.email).toLowerCase().includes(q.toLowerCase()));
  const counts = {
    all: accounts.length,
    pending: accounts.filter(a => isPending(a.status)).length,
    verified: accounts.filter(a => a.status === 'verified').length,
    rejected: accounts.filter(a => a.status === 'rejected').length,
  };

  if (!isAdmin) {
    return (
      <div className="page-in container" style={{ position: 'relative', zIndex: 1, padding: '80px 0', textAlign: 'center' }}>
        <h1 className="display" style={{ fontSize: 'clamp(24px,4vw,34px)', margin: '0 0 12px' }}>{t('admin.accessDenied')}</h1>
        <p className="muted" style={{ fontSize: 14, maxWidth: 420, margin: '0 auto 28px' }}>{t('admin.accessDeniedSub')}</p>
        <div className="row gap-3 wrap" style={{ justifyContent: 'center' }}>
          <Btn size="lg" onClick={() => go('auth')}>{t('admin.loginBtn')}</Btn>
          <Btn size="lg" variant="ghost" onClick={() => go('home')}>{t('admin.homeBtn')}</Btn>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return createPortal(
      <AdminMobile
        go={go}
        user={user}
        accounts={accounts}
        counts={counts}
        teams={teams}
        tournaments={tournaments}
        notifications={notifications}
        users={users}
        act={act}
        onModerateTeam={handleModerateTeam}
        onDeleteTeam={handleDeleteTeam}
        onUpdateTournament={handleUpdateTournament}
        sendNotification={sendNotification}
        loading={loading}
        tickets={tickets}
        markTicketRead={markSupportTicketRead}
        supportNewCount={supportNewCount}
      />,
      document.body
    );
  }

  return (
    <>
    <div className="page-in" style={{ position: 'relative', zIndex: 1, display: 'flex', minHeight: 'calc(100vh - 72px)' }}>
      {/* SIDEBAR */}
      <aside className="admin-side" style={{ flex: '0 0 232px', borderRight: '1px solid var(--line)', padding: '24px 16px', background: 'var(--bg-deep)', position: 'sticky', top: 72, alignSelf: 'flex-start', height: 'calc(100vh - 72px)' }}>
        <div className="row gap-2" style={{ padding: '0 8px 18px', marginBottom: 10, borderBottom: '1px solid var(--line)' }}>
          <span className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--accent)' }}>● ADMIN PANEL</span>
        </div>
        <nav className="col gap-1">
          {NAV.map(n => (
            <button key={n.k} onClick={() => setTab(n.k)} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 12px', borderRadius: 'var(--r-md)', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-body)', background: tab === n.k ? 'rgba(225,18,23,0.12)' : 'transparent', color: tab === n.k ? 'var(--fg)' : 'var(--fg-muted)', borderLeft: `2px solid ${tab === n.k ? 'var(--accent)' : 'transparent'}`, transition: 'all .15s' }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={tab === n.k ? 'var(--accent-bright)' : 'currentColor'} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d={n.icon} /></svg>
              {t(`admin.nav.${n.k}`)}
              {n.k === 'regs' && counts.pending > 0 && <span style={{ marginLeft: 'auto', fontSize: 11, fontFamily: 'var(--font-mono)', background: 'var(--accent)', color: '#fff', padding: '1px 7px', borderRadius: 10 }}>{counts.pending}</span>}
              {n.k === 'support' && supportNewCount > 0 && <span style={{ marginLeft: 'auto', fontSize: 11, fontFamily: 'var(--font-mono)', background: 'var(--info)', color: '#fff', padding: '1px 7px', borderRadius: 10 }}>{supportNewCount}</span>}
            </button>
          ))}
        </nav>
        <div style={{ marginTop: 'auto', position: 'absolute', bottom: 20, left: 16, right: 16 }}>
          <Btn variant="dark" full size="sm" onClick={() => go('home')}>← {t('admin.settings.backToSite')}</Btn>
        </div>
      </aside>

      {/* MAIN */}
      <main style={{ flex: 1, padding: 'clamp(20px, 3vw, 32px)', minWidth: 0, paddingBottom: 'clamp(20px, 3vw, 32px)' }} className="admin-main">
        {loading && (
          <div className="center" style={{ padding: 40 }}><Spinner size={26} /></div>
        )}
        {tab === 'overview' && (
          <div className="col gap-5">
            <div>
              <h1 className="display" style={{ fontSize: 30, margin: 0 }}>{t('admin.overview.overviewTitle')}</h1>
              <p className="muted" style={{ margin: '6px 0 0', fontSize: 14 }}>{t('admin.overview.subtitle')}</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 14 }}>
              <AdminStat label={t('admin.overview.statApps')} value={counts.all} accent />
              <AdminStat label={t('admin.overview.statPending')} value={counts.pending} />
              <AdminStat label={t('admin.overview.statTeams')} value={teams.length} sub={`${teams.filter(tm => tm.status === 'Набор').length} ${t('admin.overview.statInRecruiting')}`} />
              <AdminStat label={t('admin.overview.statTournaments')} value={tournaments.length} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr)', gap: 18 }} className="two-col">
              <Panel>
                <div className="row" style={{ justifyContent: 'space-between', marginBottom: 18 }}>
                  <h3 className="display" style={{ fontSize: 18, margin: 0 }}>{t('admin.overview.chartTitle')}</h3>
                  <Chip color="var(--accent)">{t('admin.overview.chartTotal', { n: counts.all })}</Chip>
                </div>
                <div className="col gap-3" style={{ justifyContent: 'center', height: 150 }}>
                  {[
                    [t('admin.sheet.statusPending'), counts.pending, 'var(--warn)'],
                    [t('admin.sheet.statusVerified'), counts.verified, 'var(--ok)'],
                    [t('admin.sheet.statusRejected'), counts.rejected, 'var(--danger)'],
                  ].map(([l, v, c]) => {
                    const pct = counts.all ? Math.round(v / counts.all * 100) : 0;
                    return (
                      <div key={l} className="col gap-1">
                        <div className="row" style={{ justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 13, color: 'var(--fg-soft)' }}>{l}</span>
                          <span className="mono" style={{ fontSize: 12, color: c }}>{v} · {pct}%</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 4, background: 'var(--surface-3)' }}>
                          <div style={{ height: '100%', width: `${pct}%`, borderRadius: 4, background: c, transition: 'width .5s' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Panel>
              <Panel>
                <h3 className="display" style={{ fontSize: 18, margin: '0 0 16px' }}>{t('admin.overview.recentApps')}</h3>
                <div className="col gap-2">
                  {accounts.slice(0, 5).map(a => {
                    const m = statusMeta(a.status);
                    return (
                      <div key={a.id} className="row gap-3" style={{ alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--line)', cursor: 'pointer' }} onClick={() => setSel(a)}>
                        <div className="center" style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--surface-3)', fontSize: 11, fontWeight: 700 }}>{aInitials(a.name)}</div>
                        <div className="col grow" style={{ minWidth: 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{a.nick}</span>
                          <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>{a.joined}</span>
                        </div>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.c }} />
                      </div>
                    );
                  })}
                </div>
              </Panel>
            </div>
          </div>
        )}

        {tab === 'regs' && (
          <div className="col gap-4">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 14 }}>
              <div>
                <h1 className="display" style={{ fontSize: 30, margin: 0 }}>{t('admin.regs.title')}</h1>
                <p className="muted" style={{ margin: '6px 0 0', fontSize: 14 }}>{t('admin.regs.pendingCount', { n: counts.pending })}</p>
              </div>
              <div style={{ position: 'relative', maxWidth: 260, width: '100%' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-muted)' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg></span>
                <Input placeholder={t('admin.search')} value={q} onChange={e => setQ(e.target.value)} style={{ height: 44, paddingLeft: 38 }} />
              </div>
            </div>
            <div className="row gap-2 wrap">
              {[['all', t('admin.filter.all')], ['pending', t('admin.sheet.statusPending')], ['verified', t('admin.sheet.statusVerified')], ['rejected', t('admin.sheet.statusRejected')]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} className="mono" style={{ padding: '7px 14px', borderRadius: 'var(--r-sm)', cursor: 'pointer', fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', background: filter === k ? 'var(--accent)' : 'var(--surface-2)', color: filter === k ? '#fff' : 'var(--fg-muted)', border: `1px solid ${filter === k ? 'var(--accent)' : 'var(--line)'}` }}>{l} · {counts[k]}</button>
              ))}
            </div>
            <Panel pad={0} style={{ overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                  <thead><tr style={{ background: 'var(--bg-deep)' }}>
                    {[t('admin.table.player'), t('admin.table.nickname'), 'Premier', t('admin.table.edu'), t('admin.table.doc'), t('admin.table.status'), ''].map((h, i) => (
                      <th key={i} className="mono" style={{ textAlign: i > 1 && i < 6 ? 'center' : 'left', padding: '13px 16px', fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)', textTransform: 'uppercase', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filtered.map(a => {
                      const m = statusMeta(a.status), tier = premierTier(a.rating);
                      return (
                        <tr key={a.id} style={{ borderBottom: '1px solid var(--line)', cursor: 'pointer', transition: 'background .15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          onClick={() => setSel(a)}>
                          <td style={{ padding: '12px 16px' }}>
                            <div className="row gap-3" style={{ alignItems: 'center' }}>
                              <div className="center" style={{ width: 34, height: 34, borderRadius: 8, background: `linear-gradient(135deg, ${tier.c}33, var(--surface-3))`, fontSize: 11, fontWeight: 700, border: `1px solid ${tier.c}66` }}>{aInitials(a.name)}</div>
                              <span style={{ fontSize: 13.5, fontWeight: 500 }}>{a.name}</span>
                            </div>
                          </td>
                          <td className="mono" style={{ padding: '12px 16px', fontSize: 13, color: 'var(--fg-soft)' }}>{a.nick}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>{a.rating ? <PremierBadge rating={a.rating} size="sm" /> : <span className="muted">—</span>}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12.5, color: 'var(--fg-soft)' }}>{a.edu}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>{a.doc ? <span style={{ color: 'var(--ok)' }}>✓</span> : <span style={{ color: 'var(--danger)' }}>✕</span>}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}><Chip color={m.c} style={{ fontSize: 10 }}>{t(m.l)}</Chip></td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}><span style={{ color: 'var(--fg-muted)' }}>›</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        )}

        {tab === 'users' && (
          <div className="col gap-4">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 14 }}>
              <div>
                <h1 className="display" style={{ fontSize: 30, margin: 0 }}>{t('admin.users.title')}</h1>
                <p className="muted" style={{ margin: '6px 0 0', fontSize: 14 }}>
                  {t('admin.users.captainCount', { n: users.filter(u => u.user_type === 'captain').length })} · {t('admin.users.totalCount', { n: users.length })}
                </p>
              </div>
              <div style={{ position: 'relative', maxWidth: 260, width: '100%' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-muted)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                </span>
                <Input placeholder={t('admin.search')} value={userQ} onChange={e => setUserQ(e.target.value)} style={{ height: 44, paddingLeft: 38 }} />
              </div>
            </div>
            <Panel pad={0} style={{ overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-deep)' }}>
                      {[t('admin.table.user'), t('admin.table.nickname'), 'E-mail', t('admin.table.role'), ''].map((h, i) => (
                        <th key={i} className="mono" style={{ textAlign: i === 3 ? 'center' : 'left', padding: '13px 16px', fontSize: 10, letterSpacing: '0.14em', color: 'var(--fg-muted)', textTransform: 'uppercase', fontWeight: 600, borderBottom: '1px solid var(--line)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .filter(u => (u.full_name + u.nickname + u.email).toLowerCase().includes(userQ.toLowerCase()))
                      .map(u => {
                        const isCaptain = u.user_type === 'captain';
                        const isAdmin = u.user_type === 'admin';
                        const loading = roleLoading[u.id];

                        const handleRole = async () => {
                          setRoleLoading(r => ({ ...r, [u.id]: true }));
                          try {
                            await setUserRole(u.id, isCaptain ? 'user' : 'captain');
                            // Перечитываем с сервера — бэкенд мог изменить и других юзеров (передача капитанства)
                            const fresh = await fetchUsers();
                            setUsers(fresh);
                          } finally {
                            setRoleLoading(r => ({ ...r, [u.id]: false }));
                          }
                        };

                        return (
                          <tr key={u.id} style={{ borderBottom: '1px solid var(--line)', transition: 'background .15s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <td style={{ padding: '12px 16px' }}>
                              <div className="row gap-3" style={{ alignItems: 'center' }}>
                                <div className="center" style={{ width: 34, height: 34, borderRadius: 8, background: isCaptain ? 'rgba(225,18,23,0.15)' : 'var(--surface-3)', border: `1px solid ${isCaptain ? 'var(--accent)' : 'var(--line)'}`, fontSize: 11, fontWeight: 700, color: isCaptain ? 'var(--accent-bright)' : 'var(--fg-muted)', flexShrink: 0 }}>
                                  {aInitials(u.full_name || u.nickname)}
                                </div>
                                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{u.full_name || '—'}</span>
                              </div>
                            </td>
                            <td className="mono" style={{ padding: '12px 16px', fontSize: 13, color: 'var(--fg-soft)' }}>{u.nickname || '—'}</td>
                            <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--fg-muted)' }}>{u.email || '—'}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              {isAdmin
                                ? <Chip color="var(--accent)">Admin</Chip>
                                : isCaptain
                                  ? <Chip color="var(--gold, #E6B73C)">{t('admin.users.roleCaptain')}</Chip>
                                  : <Chip color="var(--fg-muted)">{t('admin.users.rolePlayer')}</Chip>
                              }
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                              {!isAdmin && (
                                <Btn
                                  variant={isCaptain ? 'outline' : 'gold'}
                                  size="sm"
                                  disabled={loading}
                                  onClick={handleRole}
                                >
                                  {loading ? '...' : isCaptain ? t('admin.users.demoteCaptain') : t('admin.users.promoteCaptain')}
                                </Btn>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        )}

        {tab === 'teams' && (
          <div className="col gap-4">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 className="display" style={{ fontSize: 30, margin: 0 }}>{t('admin.teams.title')}</h1>
              <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)', letterSpacing: '0.1em' }}>{t('admin.teams.count', { n: teams.length })}</span>
            </div>
            {teams.length === 0 && <Panel><div style={{ textAlign: 'center', color: 'var(--fg-muted)', padding: 20 }}>{t('admin.teams.empty')}</div></Panel>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
              {teams.map(team => {
                const confirmed = team.rawStatus === 'full';
                const statusColor = confirmed ? 'var(--ok)' : team.rawStatus === 'moderation' ? 'var(--info)' : 'var(--warn)';
                return (
                  <div key={team.id} onClick={() => setSelTeam(team)} style={{
                    cursor: 'pointer', borderRadius: 'var(--r-lg)', overflow: 'hidden',
                    background: 'linear-gradient(160deg, var(--surface-2), var(--surface))',
                    border: '1px solid var(--line)',
                    boxShadow: 'var(--shadow-card)',
                    transition: 'border-color .2s, transform .15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = statusColor; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--line)'; e.currentTarget.style.transform = 'none'; }}
                  >
                    {/* Status bar */}
                    <div style={{ height: 3, background: `linear-gradient(90deg, ${statusColor}, transparent)` }} />

                    <div style={{ padding: '16px 18px' }}>
                      {/* Header */}
                      <div className="row gap-3" style={{ alignItems: 'center', marginBottom: 14 }}>
                        <div className="center display" style={{
                          width: 48, height: 48, borderRadius: 'var(--r-md)', flexShrink: 0, fontSize: 13,
                          background: 'linear-gradient(135deg, var(--accent-deep) 0%, #1a0405 100%)',
                          border: `1px solid rgba(225,18,23,0.3)`, boxShadow: '0 0 12px -4px var(--accent-glow)',
                          letterSpacing: '0.04em',
                        }}>{team.tag}</div>
                        <div className="col" style={{ minWidth: 0, flex: 1 }}>
                          <span className="display" style={{ fontSize: 16, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team.name}</span>
                          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{team.captain}</span>
                        </div>
                        <span style={{
                          flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 700,
                          letterSpacing: '0.08em', textTransform: 'uppercase', color: statusColor,
                          padding: '3px 8px', borderRadius: 4, border: `1px solid ${statusColor}33`,
                          background: `${statusColor}12`,
                        }}>{team.status}</span>
                      </div>

                      {/* Divider */}
                      <div style={{ height: 1, background: 'var(--line)', marginBottom: 14 }} />

                      {/* Stats */}
                      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="row gap-4">
                          <div className="col gap-1">
                            <span className="mono" style={{ fontSize: 9, letterSpacing: '0.16em', color: 'var(--fg-faint)' }}>{t('admin.teams.labelRoster')}</span>
                            <span style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '0.02em' }}>{team.players}/5 +{team.sub}</span>
                          </div>
                          <div className="col gap-1">
                            <span className="mono" style={{ fontSize: 9, letterSpacing: '0.16em', color: 'var(--fg-faint)' }}>{t('admin.teams.labelMap')}</span>
                            <span style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-display)', letterSpacing: '0.02em' }}>{team.map}</span>
                          </div>
                        </div>
                        <div className="row gap-2" style={{ alignItems: 'center' }}>
                          <PremierBadge rating={team.avg} size="sm" />
                          <button onClick={e => { e.stopPropagation(); setSelTeam(team); }} style={{
                            padding: '5px 11px', background: 'var(--surface-3)', border: '1px solid var(--line-strong)',
                            borderRadius: 'var(--r-sm)', color: 'var(--fg-soft)', fontSize: 11, cursor: 'pointer',
                            fontFamily: 'var(--font-mono)', letterSpacing: '0.06em', transition: 'all .15s',
                          }}>{t('admin.teams.rosterBtn')}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === 'tournaments' && (
          <div className="col gap-4">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <h1 className="display" style={{ fontSize: 30, margin: 0 }}>{t('admin.tournaments.title')}</h1>
              <Btn icon={<span style={{ fontSize: 18, lineHeight: 1 }}>+</span>} onClick={() => setShowCreate(true)}>{t('admin.tournaments.create')}</Btn>
            </div>
            <div className="col gap-3">
              {tournaments.length === 0 && <Panel><div style={{ textAlign: 'center', color: 'var(--fg-muted)', padding: 20 }}>{t('admin.tournaments.empty')}</div></Panel>}
              {tournaments.map(tournament => (
                <Panel key={tournament.id}>
                  <div className="row tournament-card-row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
                    <div className="row gap-4" style={{ alignItems: 'center', minWidth: 0, flex: '1 1 200px' }}>
                      <div style={{ width: 50, height: 50, borderRadius: 'var(--r-md)', backgroundImage: 'url(/assets/logo-federation.png)', backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid var(--line)', flexShrink: 0 }} />
                      <div className="col" style={{ minWidth: 0 }}>
                        <span className="display" style={{ fontSize: 19 }}>{tournament.title}</span>
                        <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 3 }}>{tournament.date} · {tournament.prize}</span>
                      </div>
                    </div>
                    <div className="row gap-4 wrap tournament-card-actions" style={{ alignItems: 'center' }}>
                      <div className="row gap-4">
                        <div className="col" style={{ textAlign: 'center' }}><span className="display" style={{ fontSize: 20 }}>{tournament.teams}</span><span className="mono" style={{ fontSize: 9.5, color: 'var(--fg-muted)' }}>{t('admin.tournaments.labelTeams')}</span></div>
                        <div className="col" style={{ textAlign: 'center' }}><span className="display" style={{ fontSize: 20 }}>{tournament.players}</span><span className="mono" style={{ fontSize: 9.5, color: 'var(--fg-muted)' }}>{t('admin.tournaments.labelPlayers')}</span></div>
                      </div>
                      <div className="row gap-3" style={{ alignItems: 'center' }}>
                        <Chip color={tournament.status === 'registration' ? 'var(--accent)' : 'var(--fg-muted)'}>{tournament.statusLabel}</Chip>
                        <Btn variant="ghost" size="sm" onClick={() => setSelTournament(tournament)}>{t('admin.tournaments.manage')}</Btn>
                      </div>
                    </div>
                  </div>
                </Panel>
              ))}
            </div>
          </div>
        )}

        {tab === 'support' && (
          <div className="col gap-5">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h1 className="display" style={{ fontSize: 30, margin: 0 }}>{t('admin.support.title')}</h1>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: 14 }}>{t('admin.support.subtitle')}</p>
              </div>
              <div className="row gap-2">
                {[['all', t('admin.filter.all')], ['new', t('admin.support.filterNew')], ['read', t('admin.support.filterRead')]].map(([k, l]) => (
                  <button key={k} onClick={() => setTicketFilter(k)} style={{
                    padding: '7px 16px', borderRadius: 'var(--r-md)', border: `1px solid ${ticketFilter === k ? 'var(--accent)' : 'var(--line)'}`,
                    background: ticketFilter === k ? 'rgba(225,18,23,0.12)' : 'var(--surface)', color: ticketFilter === k ? 'var(--fg)' : 'var(--fg-muted)',
                    cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-body)', transition: 'all .15s',
                  }}>{l}{k === 'new' && supportNewCount > 0 ? ` (${supportNewCount})` : ''}</button>
                ))}
              </div>
            </div>

            {tickets.filter(t => ticketFilter === 'all' || t.status === ticketFilter).length === 0 ? (
              <div className="center" style={{ padding: '60px 20px', flexDirection: 'column', gap: 12, color: 'var(--fg-muted)' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <span style={{ fontSize: 14 }}>{t('admin.support.empty')}</span>
              </div>
            ) : (
              <div className="col gap-3">
                {tickets.filter(t => ticketFilter === 'all' || t.status === ticketFilter).map(ticket => {
                  const isNew = ticket.status === 'new';
                  const isExpanded = expandedTicket === ticket.id;
                  const date = new Date(ticket.created_at);
                  const dateStr = date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                  return (
                    <div key={ticket.id} style={{
                      borderRadius: 'var(--r-lg)', border: `1px solid ${isNew ? 'rgba(88,166,255,0.35)' : 'var(--line)'}`,
                      background: isNew ? 'rgba(88,166,255,0.05)' : 'var(--surface)',
                      overflow: 'hidden', transition: 'all .2s',
                    }}>
                      <div
                        className="row"
                        style={{ padding: '16px 20px', gap: 14, alignItems: 'flex-start', cursor: 'pointer' }}
                        onClick={() => setExpandedTicket(isExpanded ? null : ticket.id)}
                      >
                        <div className="center" style={{
                          width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                          background: isNew ? 'rgba(88,166,255,0.15)' : 'var(--surface-2)',
                          border: `1px solid ${isNew ? 'rgba(88,166,255,0.4)' : 'var(--line)'}`,
                        }}>
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={isNew ? 'var(--info)' : 'var(--fg-muted)'} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                        </div>
                        <div className="col" style={{ flex: 1, minWidth: 0, gap: 4 }}>
                          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <div className="row gap-2" style={{ alignItems: 'center' }}>
                              <span style={{ fontWeight: 600, fontSize: 15 }}>{ticket.name}</span>
                              {isNew && <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', background: 'var(--info)', color: '#fff', padding: '2px 8px', borderRadius: 20, letterSpacing: '0.1em' }}>{t('admin.support.badgeNew')}</span>}
                            </div>
                            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}>{dateStr}</span>
                          </div>
                          <div className="row gap-3" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                            {ticket.phone && (
                              <span style={{ fontSize: 12.5, color: 'var(--fg)', fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                                📞 {ticket.phone}
                              </span>
                            )}
                            {ticket.email && <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>{ticket.email}</span>}
                            {ticket.page && <span className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 6 }}>/{ticket.page}</span>}
                          </div>
                          {!isExpanded && (
                            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--fg-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ticket.message}
                            </p>
                          )}
                        </div>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--fg-muted)" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s', marginTop: 2 }}>
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </div>

                      {isExpanded && (
                        <div style={{ borderTop: '1px solid var(--line)', padding: '16px 20px', animation: 'fadeUp .2s var(--ease-out) both' }}>
                          <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.7, color: 'var(--fg)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {ticket.message}
                          </p>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: ticket.user_id ? 16 : 0 }}>
                            {isNew && (
                              <button
                                onClick={async () => {
                                  await markSupportTicketRead(ticket.id);
                                  setTickets(ts => ts.map(tk => tk.id === ticket.id ? { ...tk, status: 'read' } : tk));
                                  setSupportNewCount(c => Math.max(0, c - 1));
                                }}
                                style={{
                                  padding: '8px 18px', borderRadius: 'var(--r-md)', border: '1px solid var(--info)',
                                  background: 'rgba(88,166,255,0.1)', color: 'var(--info)', cursor: 'pointer',
                                  fontSize: 13, fontFamily: 'var(--font-body)', transition: 'all .15s',
                                }}
                              >
                                ✓ {t('admin.support.markRead')}
                              </button>
                            )}
                            {!ticket.user_id && ticket.email && (
                              <a href={`mailto:${ticket.email}`} style={{
                                padding: '8px 18px', borderRadius: 'var(--r-md)', border: '1px solid var(--line)',
                                background: 'var(--surface-2)', color: 'var(--fg-muted)', cursor: 'pointer',
                                fontSize: 13, fontFamily: 'var(--font-body)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
                              }}>
                                ✉ {ticket.email}
                              </a>
                            )}
                          </div>
                          {ticket.user_id && (
                            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14 }}>
                              <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', color: 'var(--fg-muted)', marginBottom: 8 }}>
                                {t('admin.support.replyLabel')}
                              </div>
                              <textarea
                                value={replyTexts[ticket.id] || ''}
                                onChange={e => setReplyTexts(p => ({ ...p, [ticket.id]: e.target.value }))}
                                placeholder={t('admin.support.replyPlaceholder')}
                                rows={3}
                                style={{
                                  width: '100%', background: 'var(--surface-2)', border: '1px solid var(--line)',
                                  borderRadius: 'var(--r-md)', padding: '10px 14px', color: 'var(--fg)',
                                  fontSize: 13.5, fontFamily: 'var(--font-body)', resize: 'vertical', outline: 'none',
                                  transition: 'border-color .15s', boxSizing: 'border-box',
                                }}
                                onFocus={e => e.target.style.borderColor = 'var(--line-accent)'}
                                onBlur={e => e.target.style.borderColor = 'var(--line)'}
                              />
                              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                                <button
                                  disabled={!replyTexts[ticket.id]?.trim() || replyLoading[ticket.id]}
                                  onClick={() => handleTicketReply(ticket)}
                                  style={{
                                    padding: '9px 22px', borderRadius: 'var(--r-md)', border: 'none', cursor: 'pointer',
                                    fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-body)', transition: 'all .15s',
                                    background: replySent[ticket.id] ? 'var(--ok)' : (!replyTexts[ticket.id]?.trim() || replyLoading[ticket.id]) ? 'var(--surface-3)' : 'var(--accent)',
                                    color: (replySent[ticket.id] || replyTexts[ticket.id]?.trim()) ? '#fff' : 'var(--fg-faint)',
                                    boxShadow: (replyTexts[ticket.id]?.trim() && !replySent[ticket.id]) ? '0 0 18px -4px var(--accent-glow)' : 'none',
                                  }}
                                >
                                  {replySent[ticket.id] ? `✓ ${t('admin.support.replySent')}` : replyLoading[ticket.id] ? '...' : t('admin.support.sendReply')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'notify' && (
          <NotifySection users={users} sendNotification={sendNotification} notifications={notifications} />
        )}

        {tab === 'settings' && (
          <div className="col gap-4" style={{ maxWidth: 600 }}>
            <h1 className="display" style={{ fontSize: 30, margin: 0 }}>{t('admin.settings.title')}</h1>
            <Panel>
              <div className="col gap-4">
                {[[t('admin.settings.toggleReg'), true], [t('admin.settings.toggleSteam'), true], [t('admin.settings.toggleDoc'), true], [t('admin.settings.toggleTg'), false]].map(([l, on], i) => (
                  <div key={i} className="row" style={{ justifyContent: 'space-between', alignItems: 'center', paddingBottom: i < 3 ? 16 : 0, borderBottom: i < 3 ? '1px solid var(--line)' : 'none' }}>
                    <span style={{ fontSize: 14.5 }}>{l}</span>
                    <div style={{ width: 46, height: 26, borderRadius: 20, background: on ? 'var(--accent)' : 'var(--surface-3)', position: 'relative', cursor: 'pointer', transition: 'background .2s', border: '1px solid var(--line)' }}>
                      <span style={{ position: 'absolute', top: 2, left: on ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        )}
      </main>

      <TeamDetailModal team={selTeam} open={!!selTeam} onClose={() => setSelTeam(null)} onModerate={handleModerateTeam} onDelete={handleDeleteTeam} onPlayerOpen={openPlayer} />
      {/* Рендерим после модалки команды — чтобы карточка игрока открывалась поверх неё */}
      <RegDetailModal acc={sel} open={!!sel} onClose={() => setSel(null)} onAction={act} />
      <TournamentManageModal
        t={selTournament}
        teams={selTournament ? teams.filter(tm => tm.tournament_id === selTournament.id) : []}
        open={!!selTournament}
        onClose={() => setSelTournament(null)}
        onUpdate={async (id, data) => { await updateTournament(id, data); await loadAll(); }}
      />
      <TournamentCreateModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={async (data) => { await createTournament(data); await loadAll(); }}
      />
    </div>

    <nav className="admin-mobile-tabs">
      {NAV.map(n => (
        <button key={n.k} onClick={() => setTab(n.k)} className={`admin-mobile-tab-btn${tab === n.k ? ' active' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d={n.icon} />
          </svg>
          {t(`admin.nav.${n.k}`)}
          {n.k === 'regs' && counts.pending > 0 && (
            <span style={{ position: 'absolute', top: 6, right: '50%', marginRight: -18, width: 16, height: 16, borderRadius: '50%', background: 'var(--accent)', color: '#fff', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)' }}>{counts.pending}</span>
          )}
          {n.k === 'support' && supportNewCount > 0 && (
            <span style={{ position: 'absolute', top: 6, right: '50%', marginRight: -18, width: 16, height: 16, borderRadius: '50%', background: 'var(--info)', color: '#fff', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)' }}>{supportNewCount}</span>
          )}
        </button>
      ))}
    </nav>
    </>
  );
}
